/**
 * Ahrefs REST API Helpers (Story 20.1, AC #4, #5; Story 20.2 expansion)
 *
 * One helper per endpoint group. Every helper:
 *
 *  1. Goes through `safeFetch` for retry + timeout (ADR-017).
 *  2. Sends a hard-coded `select` array — NEVER an empty/default field set.
 *     Default field-sets return ALL fields (~31 for batch-analysis, ~8 for
 *     metrics) which can burn 5×–10× the units of a tight `select`. AC #4
 *     codifies this — the helpers throw `TypeError` synchronously if `select`
 *     is empty (defense-in-depth against future agents passing user-supplied
 *     field lists).
 *  3. Reads the 5 cost-telemetry response headers per AC #5 and emits a
 *     structured-log event before returning.
 *  4. NEVER logs the raw API key. The Authorization header is built inline
 *     via `buildAhrefsHeaders` and never echoed back into messages.
 *
 * The `mode` parameter is mandatory on every `/site-explorer/*` call (v3
 * removed the v2 default). Callers must pass it explicitly — the inferrer
 * lives in `normalize.ts` so adapters can reuse it.
 *
 * v3-only: this file targets `https://api.ahrefs.com/v3/*` exclusively. v2
 * was discontinued 2025-11-01 with no fallback path. See
 * https://docs.ahrefs.com/api/docs/changelog.
 *
 * **Story 20.2 changelog (cited entries used by this story; deprecated field
 * names are catalogued in `metric-allowlist.test.ts` rather than repeated
 * here so the grep guardrails stay green):**
 *  - 2025-08-05: Keyword Explorer engine-scope parameter deprecated,
 *    fully removed in 2026.
 *  - 2026: pagination cursor parameter removed from all endpoints.
 *  - 2026-02-10: Brand Radar deprecated the count-of-pages field
 *    (use `responses` instead).
 *  - 2026: Keywords History endpoint adds granular ranking groups
 *    (`top11_20`, `top21_50`, `top51_plus`) and `select` parameter support.
 *    Story 20.2 uses these by default, leaves legacy `top11_plus` for
 *    backward-compat read paths.
 *  - 2026: Organic Keywords expanded `best_position_kind` enum to include
 *    `ai_overview`, `ai_overview_sitelink`, `organic_shopping`, etc.
 */

import { API_VERSIONS } from "../config";
import { safeFetch } from "../safe-fetch";
import { adapterErr, adapterOk, type AdapterResult } from "../adapter";
import {
  parseAhrefsApiError,
  ahrefsErrorTypeToAdapterCode,
  parseAhrefsRetryAfter,
  type AhrefsApiError,
} from "./errors";
import type { AhrefsMode } from "./normalize";

// ============================================================================
// Constants
// ============================================================================

export const AHREFS_API_VERSION = API_VERSIONS.ahrefs;
export const AHREFS_API_BASE = `https://api.ahrefs.com/${AHREFS_API_VERSION}`;

/** Per-call timeout (15s — Ahrefs is generally fast; allows up to 2 retries within Workers' 30s CPU limit). */
const AHREFS_TIMEOUT_MS = 15_000;

// ============================================================================
// Header construction
// ============================================================================

/**
 * Build the request headers for an Ahrefs API call.
 *
 * The Authorization header is built inline so the apiKey value never escapes
 * this function — callers that log a request never see the raw key. Note: a
 * caller that does `console.log(headers)` would still leak the key. The
 * sanitizer in `errors.ts` is the second line of defense.
 */
export function buildAhrefsHeaders(apiKey: string): Record<string, string> {
  if (typeof apiKey !== "string" || apiKey.length === 0) {
    throw new TypeError("buildAhrefsHeaders: apiKey must be a non-empty string");
  }
  return {
    Authorization: `Bearer ${apiKey}`,
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

// ============================================================================
// Cost-telemetry header parsing (AC #5)
// ============================================================================

export interface AhrefsCostHeaders {
  readonly rows: number;
  readonly costRow: number;
  readonly costTotalExpected: number;
  readonly costTotalActual: number;
  /**
   * `cacheStatus` enumerates the four wire-format values Ahrefs returns in the
   * `x-api-cache` header (`hit | miss | no_cache | unknown`) plus a synthetic
   * `"free"` literal added in Story 20.2. The synthetic value is set by
   * helpers calling documented-as-free endpoints (Rank Tracker
   * `/competitors-stats`, Rank Tracker management endpoints, Subscription
   * Info) so the `extractActualCost` rollup short-circuits to zero — those
   * endpoints consume zero API units regardless of what `x-api-cache` says.
   */
  readonly cacheStatus: "hit" | "miss" | "no_cache" | "unknown" | "free";
}

/**
 * Parse the 5 unit-cost response headers per AC #5. Missing values default to
 * 0 / "unknown" — Ahrefs occasionally omits these headers on cached responses.
 *
 * Story 20.2: when `isFreeEndpoint=true` is passed, the parser overrides the
 * wire-format `cacheStatus` with the synthetic `"free"` value. The override
 * runs AFTER reading the headers so observability still emits whatever the
 * server claimed, but the cost rollup treats the call as zero-units.
 */
export function parseAhrefsCostHeaders(
  headers: Headers,
  options?: { readonly isFreeEndpoint?: boolean }
): AhrefsCostHeaders {
  const rows = Number(headers.get("x-api-rows")) || 0;
  const costRow = Number(headers.get("x-api-units-cost-row")) || 0;
  const costTotalExpected = Number(headers.get("x-api-units-cost-total")) || 0;
  const costTotalActual = Number(headers.get("x-api-units-cost-total-actual")) || 0;
  const rawCache = (headers.get("x-api-cache") ?? "").toLowerCase();
  const wireCache: AhrefsCostHeaders["cacheStatus"] =
    rawCache === "hit" || rawCache === "miss" || rawCache === "no_cache"
      ? rawCache
      : "unknown";
  const cacheStatus: AhrefsCostHeaders["cacheStatus"] = options?.isFreeEndpoint
    ? "free"
    : wireCache;
  return {
    rows,
    costRow,
    costTotalExpected,
    costTotalActual,
    cacheStatus,
  };
}

/** Result envelope returned from every Ahrefs helper. */
export interface AhrefsCallResult<T> {
  readonly data: T;
  readonly cost: AhrefsCostHeaders;
}

// ============================================================================
// select-array invariant
// ============================================================================

/**
 * Guard against accidental empty `select` arrays. AC #4 mandates that every
 * call set `select.length > 0`; an empty array would let Ahrefs return ALL
 * fields and burn 5×–10× the units. This is a SYNCHRONOUS throw because a
 * silent fallback would mask widget-config bugs (carry-forward from the
 * Amazon Ads 19.16.1 R1 QA Test Architect invariant).
 */
function assertSelectNonEmpty(select: ReadonlyArray<string>, endpoint: string): void {
  if (!Array.isArray(select) || select.length === 0) {
    throw new TypeError(
      `Ahrefs ${endpoint}: \`select\` must be a non-empty array — Ahrefs returns ALL fields by default which burns the customer's unit quota.`
    );
  }
}

// ============================================================================
// Logger contract (Story 20.1 AC #5 telemetry)
// ============================================================================

export interface AhrefsCallLogContext {
  readonly integrationId?: string;
  readonly tenantId?: string;
  readonly endpoint: string;
  readonly target?: string;
}

export interface AhrefsCallLogEntry extends AhrefsCallLogContext {
  readonly event: "ahrefs_api_call_units";
  readonly status: number;
  readonly rows: number;
  readonly costRow: number;
  readonly costTotalExpected: number;
  readonly costTotalActual: number;
  readonly cacheStatus: AhrefsCostHeaders["cacheStatus"];
  readonly durationMs: number;
  /**
   * Story 20.2 AC #11 — PII protection. Set ONLY for Keywords Explorer
   * Overview calls so the log records HOW MANY keywords were queried without
   * leaking the keywords themselves. Customer keywords frequently contain
   * brand names, competitor references, or sensitive search queries; logging
   * the raw array would create a GDPR exposure path. Other endpoints leave
   * this field undefined.
   */
  readonly keywordsCount?: number;
}

/** Default sink: structured JSON to stdout. */
function defaultLog(entry: AhrefsCallLogEntry): void {
  // eslint-disable-next-line no-console
  console.log(JSON.stringify(entry));
}

let logSink: (entry: AhrefsCallLogEntry) => void = defaultLog;

/** Override the log sink for tests / Grafana adapters. */
export function setAhrefsLogSink(sink: (entry: AhrefsCallLogEntry) => void): void {
  logSink = sink;
}

/** Restore the default stdout JSON sink. */
export function resetAhrefsLogSink(): void {
  logSink = defaultLog;
}

function emitCallLog(
  ctx: AhrefsCallLogContext,
  status: number,
  cost: AhrefsCostHeaders,
  durationMs: number,
  extra?: { readonly keywordsCount?: number }
): void {
  try {
    const entry: AhrefsCallLogEntry = {
      event: "ahrefs_api_call_units",
      ...ctx,
      status,
      rows: cost.rows,
      costRow: cost.costRow,
      costTotalExpected: cost.costTotalExpected,
      costTotalActual: cost.costTotalActual,
      cacheStatus: cost.cacheStatus,
      durationMs,
      ...(typeof extra?.keywordsCount === "number"
        ? { keywordsCount: extra.keywordsCount }
        : {}),
    };
    logSink(entry);
  } catch {
    // Never let observability throwing break the data path.
  }
}

// ============================================================================
// Generic GET helper
// ============================================================================

/**
 * Options for the generic per-call wrapper. Extended in Story 20.2 to carry
 * the free-endpoint flag and the optional `keywordsCount` log field.
 */
interface AhrefsCallOptions {
  /** Mark the response cost as `"free"` regardless of `x-api-cache` header. */
  readonly isFreeEndpoint?: boolean;
  /** Optional integer surfaced in the structured log. PII-safe. */
  readonly keywordsCount?: number;
}

async function ahrefsGet<T>(
  apiKey: string,
  path: string,
  params: Record<string, string>,
  ctx: AhrefsCallLogContext,
  options?: AhrefsCallOptions
): Promise<AdapterResult<AhrefsCallResult<T>>> {
  const url = `${AHREFS_API_BASE}${path}?${new URLSearchParams(params).toString()}`;
  const startedAt = Date.now();

  const result = await safeFetch(
    url,
    {
      method: "GET",
      headers: buildAhrefsHeaders(apiKey),
    },
    AHREFS_TIMEOUT_MS
  );

  if (!result.success) {
    const durationMs = Date.now() - startedAt;
    emitCallLog(ctx, result.error.providerStatus ?? 0, emptyCost(), durationMs, {
      keywordsCount: options?.keywordsCount,
    });
    return mapSafeFetchError(result.error, apiKey);
  }

  return parseAhrefsResponse<T>(result.data, ctx, startedAt, options);
}

async function ahrefsPost<T>(
  apiKey: string,
  path: string,
  body: unknown,
  ctx: AhrefsCallLogContext,
  options?: AhrefsCallOptions
): Promise<AdapterResult<AhrefsCallResult<T>>> {
  const url = `${AHREFS_API_BASE}${path}`;
  const startedAt = Date.now();

  const result = await safeFetch(
    url,
    {
      method: "POST",
      headers: buildAhrefsHeaders(apiKey),
      body: JSON.stringify(body),
    },
    AHREFS_TIMEOUT_MS
  );

  if (!result.success) {
    const durationMs = Date.now() - startedAt;
    emitCallLog(ctx, result.error.providerStatus ?? 0, emptyCost(), durationMs, {
      keywordsCount: options?.keywordsCount,
    });
    return mapSafeFetchError(result.error, apiKey);
  }

  return parseAhrefsResponse<T>(result.data, ctx, startedAt, options);
}

function emptyCost(): AhrefsCostHeaders {
  return {
    rows: 0,
    costRow: 0,
    costTotalExpected: 0,
    costTotalActual: 0,
    cacheStatus: "unknown",
  };
}

async function parseAhrefsResponse<T>(
  response: Response,
  ctx: AhrefsCallLogContext,
  startedAt: number,
  options?: AhrefsCallOptions
): Promise<AdapterResult<AhrefsCallResult<T>>> {
  const cost = parseAhrefsCostHeaders(response.headers, {
    isFreeEndpoint: options?.isFreeEndpoint,
  });
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    const durationMs = Date.now() - startedAt;
    emitCallLog(ctx, response.status, cost, durationMs, {
      keywordsCount: options?.keywordsCount,
    });
    return adapterErr({
      code: "invalid_response",
      message: `Ahrefs ${ctx.endpoint}: response was not valid JSON`,
      retryable: false,
      providerStatus: response.status,
    });
  }
  const durationMs = Date.now() - startedAt;
  emitCallLog(ctx, response.status, cost, durationMs, {
    keywordsCount: options?.keywordsCount,
  });
  return adapterOk({ data: body as T, cost });
}

/**
 * Map a safe-fetch HTTP error to an AdapterError, using the structured-code
 * parser when the safe-fetch layer captured a body. Falls back to the
 * existing safe-fetch mapping otherwise.
 */
function mapSafeFetchError(
  err: { code: string; providerStatus?: number; providerMessage?: string; retryAfterMs?: number; retryable: boolean; message: string },
  apiKey: string
): AdapterResult<never> {
  // safeFetch already sanitized providerMessage. Try to parse JSON for tier-1.
  let parsedBody: unknown = undefined;
  if (err.providerMessage) {
    try {
      parsedBody = JSON.parse(err.providerMessage);
    } catch {
      parsedBody = undefined;
    }
  }
  const status = err.providerStatus ?? 0;
  const ahrefs: AhrefsApiError | null =
    status > 0 ? parseAhrefsApiError(parsedBody ?? {}, status, apiKey) : null;

  if (ahrefs) {
    return adapterErr({
      code: ahrefsErrorTypeToAdapterCode(ahrefs.type),
      message: ahrefs.message,
      retryable: ahrefs.retryable,
      retryAfterMs: err.retryAfterMs ?? parseAhrefsRetryAfter(null),
      providerStatus: ahrefs.status ?? undefined,
      providerMessage: err.providerMessage,
    });
  }

  return adapterErr({
    code: err.code as never,
    message: err.message,
    retryable: err.retryable,
    retryAfterMs: err.retryAfterMs,
    providerStatus: err.providerStatus,
    providerMessage: err.providerMessage,
  });
}

// ============================================================================
// Endpoint helpers (AC #4)
// ============================================================================

/**
 * `GET /v3/site-explorer/domain-rating` — DR + ahrefs_rank.
 * 50 units per call (minimum cost per row × 1 row).
 */
export async function fetchAhrefsDomainRating(
  apiKey: string,
  target: string,
  mode: AhrefsMode,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<AdapterResult<AhrefsCallResult<{ readonly domain_rating?: number; readonly ahrefs_rank?: number }>>> {
  const select = ["domain_rating", "ahrefs_rank"];
  assertSelectNonEmpty(select, "/site-explorer/domain-rating");
  return ahrefsGet(
    apiKey,
    "/site-explorer/domain-rating",
    {
      target,
      mode,
      output: "json",
      select: select.join(","),
    },
    { ...ctx, endpoint: "/site-explorer/domain-rating", target }
  );
}

/**
 * `GET /v3/site-explorer/backlinks-stats` — 4-field response (live, all_time,
 * live_refdomains, all_time_refdomains). v3-specific shape — see AC #4
 * critical v2→v3 change documentation.
 */
export async function fetchAhrefsBacklinksStats(
  apiKey: string,
  target: string,
  mode: AhrefsMode,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<AdapterResult<AhrefsCallResult<{
  readonly live?: number;
  readonly all_time?: number;
  readonly live_refdomains?: number;
  readonly all_time_refdomains?: number;
}>>> {
  const select = ["live", "all_time", "live_refdomains", "all_time_refdomains"];
  assertSelectNonEmpty(select, "/site-explorer/backlinks-stats");
  return ahrefsGet(
    apiKey,
    "/site-explorer/backlinks-stats",
    {
      target,
      mode,
      output: "json",
      select: select.join(","),
    },
    { ...ctx, endpoint: "/site-explorer/backlinks-stats", target }
  );
}

/**
 * `GET /v3/site-explorer/metrics` — organic + paid traffic & keywords. 8
 * fields. `org_cost` and `paid_cost` are 10-unit fields each per
 * `_bmad/bmm/data/integration-metrics/ahrefs.md`.
 */
export async function fetchAhrefsMetrics(
  apiKey: string,
  target: string,
  mode: AhrefsMode,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<AdapterResult<AhrefsCallResult<Record<string, number | null | undefined>>>> {
  const select = [
    "org_keywords",
    "paid_keywords",
    "org_keywords_1_3",
    "org_traffic",
    "org_cost",
    "paid_traffic",
    "paid_cost",
    "paid_pages",
  ];
  assertSelectNonEmpty(select, "/site-explorer/metrics");
  return ahrefsGet(
    apiKey,
    "/site-explorer/metrics",
    {
      target,
      mode,
      output: "json",
      select: select.join(","),
    },
    { ...ctx, endpoint: "/site-explorer/metrics", target }
  );
}

/**
 * `POST /v3/batch-analysis` — multi-target dashboard endpoint. Cost scales
 * as `max(50, perRowCost × rows)` where `rows` = `targets.length`.
 *
 * `select` is mandatory (AC #4); the caller passes the exact list. The
 * 18-field default the adapter uses is documented in AC #4 Group D.
 */
export async function fetchAhrefsBatchAnalysis(
  apiKey: string,
  targets: ReadonlyArray<{ readonly url: string; readonly mode: AhrefsMode }>,
  select: ReadonlyArray<string>,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<AdapterResult<AhrefsCallResult<{ readonly batch_analysis?: ReadonlyArray<Record<string, unknown>> }>>> {
  assertSelectNonEmpty(select, "/batch-analysis");
  if (!Array.isArray(targets) || targets.length === 0) {
    throw new TypeError("Ahrefs /batch-analysis: `targets` must be a non-empty array");
  }
  return ahrefsPost(
    apiKey,
    "/batch-analysis",
    {
      select,
      targets: targets.map((t) => ({ url: t.url, mode: t.mode })),
    },
    {
      ...ctx,
      endpoint: "/batch-analysis",
      target: targets.map((t) => t.url).join(","),
    }
  );
}

// ============================================================================
// Free validation endpoint (AC #2, #6)
// ============================================================================

export interface AhrefsSubscriptionInfo {
  readonly subscription?: string;
  readonly usage_reset_date?: string;
  readonly units_limit_workspace?: number;
  readonly units_usage_workspace?: number;
  readonly units_limit_api_key?: number;
  readonly units_usage_api_key?: number;
  readonly api_key_expiration_date?: string;
}

/**
 * `GET /v3/subscription-info/limits-and-usage` — FREE endpoint (does NOT
 * consume units per docs.ahrefs.com/api/docs/limits-consumption). Used for:
 *
 *  - Connect-time validation (AC #2) so a wrong-key check costs zero quota
 *  - Cron health-check (AC #6) — runs every 15 min; paid health-check would
 *    burn the customer's quota for nothing
 *  - In-place key rotation (AC #15)
 */
export async function validateAhrefsConnection(
  apiKey: string,
  ctx?: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<AdapterResult<AhrefsCallResult<AhrefsSubscriptionInfo>>> {
  return ahrefsGet<AhrefsSubscriptionInfo>(
    apiKey,
    "/subscription-info/limits-and-usage",
    { output: "json" },
    {
      ...ctx,
      endpoint: "/subscription-info/limits-and-usage",
    },
    { isFreeEndpoint: true }
  );
}

// ============================================================================
// Story 20.2 — Endpoint enum + generic wrapper (AC #1, Task 1.5)
// ============================================================================

/**
 * The 10 endpoint keys the Ahrefs adapter exposes after Story 20.2a ships.
 * Mirrors the `_reportType` discriminator values produced by the
 * transformers; widget rendering layers dispatch on this string.
 */
export type AhrefsEndpoint =
  | "domain-rating"
  | "backlinks-stats"
  | "metrics"
  | "batch-analysis"
  | "keywords-explorer-overview"
  | "organic-keywords"
  | "rank-tracker-competitors-stats"
  | "keywords-history"
  // Story 20.2a — time-series + per-country breakdowns
  | "metrics-history"
  | "metrics-by-country"
  // Story 20.2c — Rank Tracker per-keyword `/overview` (FREE)
  | "rank-tracker-overview"
  // Story 20.2d — Site Audit family (4 endpoints):
  //   - `/projects` (FREE per docs.ahrefs.com — verified 2026-05-05)
  //   - `/issues` / `/page-explorer` / `/page-content` (paid, fixed 50u/request)
  | "site-audit-projects"
  | "site-audit-issues"
  | "site-audit-page-explorer"
  | "site-audit-page-content"
  // Story 20.2e — Brand Radar AI-visibility surface (8 GET endpoints; 9
  // `_reportType` discriminators because `/sov-overview` + `/sov-history` are
  // 2 distinct surfaces). Paid add-on subscription (NOT bundled with
  // Enterprise) per [help.ahrefs.com/en/articles/11064852-about-brand-radar]
  // verified 2026-05-05. Per-field 10u surcharge applies to `response`,
  // `links`, `volume` on /ai-responses; `prompts=custom` makes the call FREE.
  | "brand-radar-ai-responses"
  | "brand-radar-impressions-history"
  | "brand-radar-mentions-history"
  | "brand-radar-impressions-overview"
  | "brand-radar-mentions-overview"
  | "brand-radar-sov-overview"
  | "brand-radar-sov-history"
  | "brand-radar-cited-domains"
  | "brand-radar-cited-pages"
  // Story 20.2f — Web Analytics overview surface (`/stats` + `/chart`).
  // Both FREE per [docs.ahrefs.com/api/reference/web-analytics] verified
  // 2026-05-06. Bundled with every Ahrefs account (including Free tier) up to
  // 1M events/month per site per
  // [help.ahrefs.com/en/articles/10247870-about-ahrefs-web-analytics]. The
  // 30 per-dimension `/web-analytics/<dim>(+chart)` endpoints are intentionally
  // deferred per ADR-B — each per-dim endpoint returns
  // `{ stats: Array<{ <dim>, ...metrics }> }`, a different shape from the
  // singleton `/stats`, requiring its own transformer pair (NOT a 1-line
  // dispatcher add). Project discovery uses `/v3/management/projects` which
  // does NOT add an enum entry — it is called directly via `ahrefsGet` (mirrors
  // how `fetchAhrefsRankTrackerProjects` calls `/keyword-list` without an enum
  // entry, since management endpoints are not part of the per-endpoint
  // cache-policy / cost-policy machinery).
  | "web-analytics-stats"
  | "web-analytics-chart"
  // Story 35.8 — Subscription Info `/limits-and-usage` (FREE endpoint).
  // Workspace + API-key telemetry surface. Single endpoint in the
  // Subscription Info group per docs.ahrefs.com/api/reference/subscription-info
  // verified 2026-05-19. Available to any Ahrefs Connect customer (Lite,
  // Standard, Advanced, Enterprise) — no add-on probe required. Bypasses
  // `checkAhrefsUnitBudget` per the FREE-endpoint convention.
  | "subscription-info-limits-and-usage";

/**
 * Map `AhrefsEndpoint` to its REST path. Used by the cache-policy layer + the
 * generic wrapper.
 */
const ENDPOINT_PATH: Record<AhrefsEndpoint, string> = {
  "domain-rating": "/site-explorer/domain-rating",
  "backlinks-stats": "/site-explorer/backlinks-stats",
  metrics: "/site-explorer/metrics",
  "batch-analysis": "/batch-analysis",
  "keywords-explorer-overview": "/keywords-explorer/overview",
  "organic-keywords": "/site-explorer/organic-keywords",
  "rank-tracker-competitors-stats": "/rank-tracker/competitors-stats",
  "keywords-history": "/site-explorer/keywords-history",
  // Story 20.2a
  "metrics-history": "/site-explorer/metrics-history",
  "metrics-by-country": "/site-explorer/metrics-by-country",
  // Story 20.2c — per-keyword Rank Tracker list (FREE endpoint)
  "rank-tracker-overview": "/rank-tracker/overview",
  // Story 20.2d — Site Audit
  "site-audit-projects": "/site-audit/projects",
  "site-audit-issues": "/site-audit/issues",
  "site-audit-page-explorer": "/site-audit/page-explorer",
  "site-audit-page-content": "/site-audit/page-content",
  // Story 20.2e — Brand Radar (8 GET endpoints; 9 _reportType discriminators
  // because /sov-overview and /sov-history are 2 distinct surfaces).
  "brand-radar-ai-responses": "/brand-radar/ai-responses",
  "brand-radar-impressions-history": "/brand-radar/impressions-history",
  "brand-radar-mentions-history": "/brand-radar/mentions-history",
  "brand-radar-impressions-overview": "/brand-radar/impressions-overview",
  "brand-radar-mentions-overview": "/brand-radar/mentions-overview",
  "brand-radar-sov-overview": "/brand-radar/sov-overview",
  "brand-radar-sov-history": "/brand-radar/sov-history",
  "brand-radar-cited-domains": "/brand-radar/cited-domains",
  "brand-radar-cited-pages": "/brand-radar/cited-pages",
  // Story 20.2f — Web Analytics overview surface.
  "web-analytics-stats": "/web-analytics/stats",
  "web-analytics-chart": "/web-analytics/chart",
  // Story 35.8 — Subscription Info `/limits-and-usage` (FREE).
  "subscription-info-limits-and-usage": "/subscription-info/limits-and-usage",
};

/**
 * Resolve the wire path for an `AhrefsEndpoint`. Exported so the cache-policy
 * + proxy routes can derive paths without re-encoding string literals.
 */
export function ahrefsEndpointPath(endpoint: AhrefsEndpoint): string {
  return ENDPOINT_PATH[endpoint];
}

/**
 * Generic per-endpoint wrapper introduced in Story 20.2 (Round 2 elicitation).
 * Each named helper below is a thin adapter that builds its own params
 * object and delegates here. This factoring saves ~80 LOC of identical
 * cost-headers/error-mapping/structured-log boilerplate per new helper.
 *
 * The Story 20.1 helpers (domain-rating / backlinks-stats / metrics /
 * batch-analysis) are NOT refactored to use this wrapper — refactoring
 * shipped code is OUT OF SCOPE for Story 20.2 (Task 1.6).
 *
 * @internal Exported for tests; production consumers use named wrappers.
 */
export async function fetchAhrefsEndpoint<T>(
  apiKey: string,
  endpoint: AhrefsEndpoint,
  params: Record<string, string>,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId" | "target">,
  options?: AhrefsCallOptions
): Promise<AdapterResult<AhrefsCallResult<T>>> {
  const path = ENDPOINT_PATH[endpoint];
  return ahrefsGet<T>(
    apiKey,
    path,
    params,
    { ...ctx, endpoint: path },
    options
  );
}

// ============================================================================
// Story 20.2 — Keywords Explorer Overview (AC #2)
// ============================================================================

export type AhrefsKeywordsExplorerTargetMode =
  | "domain"
  | "subdomain"
  | "prefix"
  | "exact_url";

/**
 * Story 20.2 AC #2 — DEFAULT `select` for `/keywords-explorer/overview`.
 *
 * 18 fields chosen for max widget value at minimum unit cost. The three
 * high-cost fields excluded by default:
 *  - `volume_monthly_history` — 2 units/month × keywords (50-unit floor)
 *  - `volume_monthly` — 10 units/keyword
 *  - `first_seen` — 5 units/keyword
 *
 * Story 35.8 catalogues these 3 fields as `live` MetricDefinitions in
 * `AHREFS_METRICS` (rows 79, 93, 94) but does NOT add them to the default
 * select — they remain widget-config opt-in via the
 * `KEYWORDS_EXPLORER_OVERVIEW_OPT_IN_FIELDS` array. Story 20.4 widget config
 * UI surfaces these to the agency principal as an opt-in checkbox group with
 * the per-field unit cost displayed.
 */
export const KEYWORDS_EXPLORER_OVERVIEW_DEFAULT_SELECT: ReadonlyArray<string> = [
  "keyword",
  "clicks",
  "cpc",
  "cps",
  "difficulty",
  "global_volume",
  "intents",
  "parent_topic",
  "parent_volume",
  "searches_pct_clicks_organic_and_paid",
  "searches_pct_clicks_organic_only",
  "searches_pct_clicks_paid_only",
  "serp_features",
  "serp_last_update",
  "traffic_potential",
  "volume",
  "volume_desktop_pct",
  "volume_mobile_pct",
];

/**
 * Story 35.8 — Keywords Explorer long-tail OPT-IN field set (NOT in the
 * default-select).
 *
 * Per code-review pass 1 (H1): the 3 fields below carry distinct per-keyword
 * surcharges per docs.ahrefs.com/api/reference/keywords-explorer/get-overview
 * verified 2026-05-19:
 *   - `first_seen`: 5 units/keyword
 *   - `volume_monthly`: 10 units/keyword
 *   - `volume_monthly_history`: 2 units per month × keywords (50-unit floor)
 *
 * Pass 3 (L2) — corrected cost math: at 200 keywords × 13-month default
 * history range, the worst-case surcharge is `200 × (5 + 10 + 13×2) = 8,200u`
 * (NOT the +6,000u figure earlier passes claimed). At 50-keyword steady-state
 * the surcharge is `50 × (5 + 10 + 13×2) = 2,050u`. Promoting these into the
 * default-select would silently regress cost for ALL existing widget consumers.
 * The Lite/Standard tier API-key sub-limit (`units_limit_api_key`) can be
 * lower than the workspace `units_limit_workspace`, so a default-select
 * widening can exhaust the per-key budget even when the workspace budget is
 * healthy.
 *
 * Story 20.4 widget config UI surfaces these as opt-in via the widened
 * `keywords-explorer-overview` ALLOWLIST arm. Catalogue rows 79, 93 ship as
 * `live` (per Story 35.8 AC #1); row 94 stays `gap` per ADR-E (opaque-array
 * shape; per-element transformer deferred to Story 20.4). `MetricDefinition`
 * rows for the 3 fields exist in `AHREFS_METRICS` so widgets can request them
 * via the existing `select` opt-in mechanism; the transformer
 * (`transformers.ts`) emits the 3 wire fields when present, regardless of
 * whether they came from the default-select or a custom `select` array.
 *
 * `volume_monthly_history` ships as an opaque Array<{date, value}>; the
 * Story 20.3 line-chart widget renders it via direct wire-key access.
 */
export const KEYWORDS_EXPLORER_OVERVIEW_OPT_IN_FIELDS: ReadonlyArray<string> = [
  "first_seen",
  "volume_monthly",
  "volume_monthly_history",
];

export interface KeywordsExplorerOverviewParams {
  /** 1-200 keywords. Validated synchronously (TypeError on overflow/empty). */
  readonly keywords: ReadonlyArray<string>;
  /** ISO 3166-1 alpha-2 lowercase. Required by the endpoint. */
  readonly country: string;
  /** Mandatory; throws TypeError on empty array. */
  readonly select: ReadonlyArray<string>;
  readonly target?: string;
  readonly target_mode?: AhrefsKeywordsExplorerTargetMode;
  /** 1-1000; default 1000. */
  readonly limit?: number;
  /** YYYY-MM-DD bounds for `volume_monthly` history opt-in. */
  readonly volume_monthly_date_from?: string;
  readonly volume_monthly_date_to?: string;
  /** Filter expression. Goes through `normalizeAhrefsWhereClause` upstream. */
  readonly where?: string;
}

/**
 * `GET /v3/keywords-explorer/overview` — keyword research / volume / SERP
 * features / intents per [docs.ahrefs.com/api/reference/keywords-explorer/get-overview].
 *
 * Story 20.2 AC #2 — paid endpoint. Cost ≈ `max(50, 10 × keywords)` for the
 * default select (5 of 18 fields are 10-unit fields). Validates
 * `keywords.length` ∈ [1, 200] synchronously to fail fast on widget-config
 * bugs (a zero-keyword call would burn 50 units for an empty response).
 *
 * **PII protection (AC #11):** the structured log records `keywordsCount`
 * (integer) but NOT the raw keywords array — customer keywords frequently
 * contain brand names, competitor references, or sensitive search queries
 * which would create a GDPR exposure path if logged.
 */
export async function fetchAhrefsKeywordsExplorerOverview(
  apiKey: string,
  params: KeywordsExplorerOverviewParams,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<
  AdapterResult<
    AhrefsCallResult<{ readonly keywords?: ReadonlyArray<Record<string, unknown>> }>
  >
> {
  if (!Array.isArray(params.keywords) || params.keywords.length === 0) {
    throw new TypeError(
      "Ahrefs /keywords-explorer/overview: `keywords` must be a non-empty array — a zero-keyword call burns 50 units for an empty response."
    );
  }
  if (params.keywords.length > 200) {
    throw new TypeError(
      `Ahrefs /keywords-explorer/overview: \`keywords\` exceeds the 200-keyword hard cap (${params.keywords.length} supplied). Per docs.ahrefs.com/api/reference/keywords-explorer/get-overview.`
    );
  }
  if (!params.country || !/^[a-z]{2}$/i.test(params.country)) {
    throw new TypeError(
      "Ahrefs /keywords-explorer/overview: `country` must be a 2-letter ISO 3166-1 alpha-2 code (e.g. \"us\")."
    );
  }
  assertSelectNonEmpty(params.select, "/keywords-explorer/overview");

  const keywordsParam = params.keywords
    .map((k) => encodeURIComponent(k))
    .join(",");
  const wireParams: Record<string, string> = {
    keywords: keywordsParam,
    country: params.country.toLowerCase(),
    select: params.select.join(","),
    output: "json",
  };
  if (params.target) wireParams.target = params.target;
  if (params.target_mode) wireParams.target_mode = params.target_mode;
  if (params.limit !== undefined) wireParams.limit = String(params.limit);
  if (params.volume_monthly_date_from)
    wireParams.volume_monthly_date_from = params.volume_monthly_date_from;
  if (params.volume_monthly_date_to)
    wireParams.volume_monthly_date_to = params.volume_monthly_date_to;
  if (params.where) wireParams.where = params.where;

  return fetchAhrefsEndpoint(
    apiKey,
    "keywords-explorer-overview",
    wireParams,
    { ...ctx, target: params.target },
    { keywordsCount: params.keywords.length }
  );
}

// ============================================================================
// Story 20.2 — Organic Keywords (AC #3)
// ============================================================================

/**
 * Story 20.2 AC #3 — DEFAULT `select` for `/site-explorer/organic-keywords`.
 *
 * 20 fields. Cost analysis (per docs.ahrefs.com/api/reference/site-explorer/get-organic-keywords):
 *   - 5 of these are 10-unit fields (`volume`, `sum_traffic`, `sum_paid_traffic`,
 *     `keyword_difficulty`, `cpc`)
 *   - Remaining 15 are 1-unit fields
 *   - Per-row cost ≈ `60 + 10×5 = 110 units`
 *   - 100-row response ≈ 11_000 units (well under the 5M/day budget)
 *
 * Daily-burn projection at 4-hour cache TTL with 5 clients × 1 organic-keywords
 * call per sync × 6 syncs/day = 5 × 6 × 11_000 = 330_000 units/day ≪ 5M cap.
 */
export const ORGANIC_KEYWORDS_DEFAULT_SELECT: ReadonlyArray<string> = [
  "keyword",
  "best_position",
  "best_position_diff",
  "best_position_kind",
  "best_position_url",
  "is_best_position_set_top_3",
  "is_best_position_set_top_4_10",
  "is_best_position_set_top_11_50",
  "serp_features",
  "serp_features_count",
  "keyword_difficulty",
  "keyword_country",
  "volume",
  "volume_desktop_pct",
  "volume_mobile_pct",
  "sum_traffic",
  "sum_paid_traffic",
  "cpc",
  "is_commercial",
  "is_informational",
];

/**
 * Hard-cap for adapter-driven `/organic-keywords` calls. Docs allow 1000;
 * we cap at 100 to keep autonomous-sync cost predictable. Widget-config
 * callers can opt-up via the future Story 20.5 widget UI.
 */
export const ORGANIC_KEYWORDS_ADAPTER_LIMIT_CAP = 100;

export type AhrefsOrganicKeywordsMode =
  | "domain"
  | "subdomain"
  | "subdomains"
  | "prefix"
  | "exact_url";

export interface OrganicKeywordsParams {
  readonly target: string;
  readonly date: string; // YYYY-MM-DD
  readonly select: ReadonlyArray<string>;
  /** Default `"subdomains"` per docs (unique to this endpoint). */
  readonly mode?: AhrefsOrganicKeywordsMode;
  /** Default `"both"` per docs. */
  readonly protocol?: "http" | "https" | "both";
  readonly country?: string;
  readonly date_compared?: string;
  readonly volume_mode?: "monthly" | "average";
  readonly limit?: number;
  readonly order_by?: string;
  readonly where?: string;
}

/**
 * `GET /v3/site-explorer/organic-keywords` — every keyword the customer's
 * tracked domain ranks for organically, with `best_position_diff` deltas.
 *
 * Story 20.2 AC #3 — paid endpoint. Default `mode: "subdomains"` (per docs)
 * and `protocol: "both"`. `limit` is hard-capped at
 * `ORGANIC_KEYWORDS_ADAPTER_LIMIT_CAP = 100` for adapter-driven calls.
 */
export async function fetchAhrefsOrganicKeywords(
  apiKey: string,
  params: OrganicKeywordsParams,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<
  AdapterResult<
    AhrefsCallResult<{ readonly keywords?: ReadonlyArray<Record<string, unknown>> }>
  >
> {
  if (!params.target || params.target.length === 0) {
    throw new TypeError("Ahrefs /site-explorer/organic-keywords: `target` is required.");
  }
  if (!params.date || !/^\d{4}-\d{2}-\d{2}$/.test(params.date)) {
    throw new TypeError(
      "Ahrefs /site-explorer/organic-keywords: `date` must be in YYYY-MM-DD format."
    );
  }
  assertSelectNonEmpty(params.select, "/site-explorer/organic-keywords");

  const limit = Math.min(
    params.limit ?? ORGANIC_KEYWORDS_ADAPTER_LIMIT_CAP,
    ORGANIC_KEYWORDS_ADAPTER_LIMIT_CAP
  );
  const wireParams: Record<string, string> = {
    target: params.target,
    date: params.date,
    select: params.select.join(","),
    mode: params.mode ?? "subdomains",
    protocol: params.protocol ?? "both",
    output: "json",
    limit: String(limit),
  };
  if (params.country) wireParams.country = params.country.toLowerCase();
  if (params.date_compared) wireParams.date_compared = params.date_compared;
  if (params.volume_mode) wireParams.volume_mode = params.volume_mode;
  if (params.order_by) wireParams.order_by = params.order_by;
  if (params.where) wireParams.where = params.where;

  return fetchAhrefsEndpoint(
    apiKey,
    "organic-keywords",
    wireParams,
    { ...ctx, target: params.target }
  );
}

// ============================================================================
// Story 20.2 — Rank Tracker Competitors Stats (AC #4) — FREE endpoint
// ============================================================================

/**
 * Story 20.2 AC #4 — DEFAULT `select` for `/rank-tracker/competitors-stats`.
 *
 * All 23 documented fields — this endpoint is FREE per
 * [docs.ahrefs.com/api/reference/rank-tracker/get-competitors-stats], so
 * selecting all fields costs zero units and gives widget config maximum
 * flexibility.
 */
export const RANK_TRACKER_COMPETITORS_STATS_DEFAULT_SELECT: ReadonlyArray<string> = [
  "competitor",
  "average_position",
  "share_of_voice",
  "share_of_traffic_value",
  "traffic",
  "traffic_value",
  "ai_overview_count",
  "pos_1_3",
  "pos_4_10",
  "pos_11_20",
  "pos_21_50",
  "pos_51_plus",
  "pos_no_rank",
  "discussions_count",
  "featured_snippet_count",
  "image_pack_count",
  "knowledge_card_count",
  "knowledge_panel_count",
  "sitelinks_count",
  "thumbnail_count",
  "top_stories_count",
  "video_preview_count",
  "videos_count",
  "x_count",
];

export interface RankTrackerCompetitorsStatsParams {
  readonly project_id: number;
  readonly date: string; // YYYY-MM-DD
  /** Required — Ahrefs returns 400 without it. NO `"both"` option. */
  readonly device: "desktop" | "mobile";
  readonly select: ReadonlyArray<string>;
  readonly volume_mode?: "monthly" | "average";
}

/**
 * `GET /v3/rank-tracker/competitors-stats` — aggregated SERP summary across
 * the customer's tracked keywords + 10 competitors (Share of Voice, position
 * buckets, SERP-feature presence counts).
 *
 * Story 20.2 AC #4 — **FREE endpoint, zero API units** per
 * [docs.ahrefs.com/api/reference/rank-tracker/get-competitors-stats]. The
 * helper marks `cacheStatus: "free"` so the cost-rollup short-circuits to
 * zero regardless of what the response headers say.
 *
 * `device` is mandatory and the helper does NOT default it. Rank Tracker
 * projects are configured per-device with no universal "both" mode.
 */
export async function fetchAhrefsRankTrackerCompetitorsStats(
  apiKey: string,
  params: RankTrackerCompetitorsStatsParams,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<
  AdapterResult<
    AhrefsCallResult<{ readonly competitors?: ReadonlyArray<Record<string, unknown>> }>
  >
> {
  if (typeof params.project_id !== "number" || !Number.isFinite(params.project_id)) {
    throw new TypeError(
      "Ahrefs /rank-tracker/competitors-stats: `project_id` is required (numeric)."
    );
  }
  if (!params.date || !/^\d{4}-\d{2}-\d{2}$/.test(params.date)) {
    throw new TypeError(
      "Ahrefs /rank-tracker/competitors-stats: `date` must be in YYYY-MM-DD format."
    );
  }
  if (params.device !== "desktop" && params.device !== "mobile") {
    throw new TypeError(
      "Rank Tracker requires explicit device scope (desktop | mobile) — Ahrefs does not support a unified 'both' device mode."
    );
  }
  assertSelectNonEmpty(params.select, "/rank-tracker/competitors-stats");

  const wireParams: Record<string, string> = {
    project_id: String(params.project_id),
    date: params.date,
    device: params.device,
    select: params.select.join(","),
    output: "json",
  };
  if (params.volume_mode) wireParams.volume_mode = params.volume_mode;

  return fetchAhrefsEndpoint(
    apiKey,
    "rank-tracker-competitors-stats",
    wireParams,
    ctx,
    { isFreeEndpoint: true }
  );
}

// ----------------------------------------------------------------------------
// Story 20.2 — Rank Tracker project discovery (AC #4)
// ----------------------------------------------------------------------------

export interface AhrefsRankTrackerProject {
  readonly projectId: number;
  readonly name: string;
  readonly targetUrl: string;
  readonly device: "desktop" | "mobile";
  readonly country: string;
}

interface AhrefsRankTrackerProjectsRaw {
  readonly project_list?: ReadonlyArray<{
    readonly id?: number;
    readonly project_id?: number;
    readonly name?: string;
    readonly title?: string;
    readonly target_url?: string;
    readonly target?: string;
    readonly device?: string;
    readonly country?: string;
  }>;
  readonly projects?: ReadonlyArray<{
    readonly id?: number;
    readonly project_id?: number;
    readonly name?: string;
    readonly target_url?: string;
    readonly device?: string;
    readonly country?: string;
  }>;
}

/**
 * `GET /v3/keyword-list` — Rank Tracker management endpoint that surfaces the
 * customer's project list. FREE per Ahrefs Management endpoints documentation.
 *
 * Used by the widget config UI (Story 20.5) to populate a "select your Rank
 * Tracker project" dropdown. Story 20.2 ships only the helper + proxy route;
 * the UI lives in Story 20.5.
 *
 * Defensively unifies the two response shapes Ahrefs has returned over the
 * years (`project_list` vs `projects`) — agents tracing this code will find
 * the historical context in the Story 20.2 dev notes.
 */
export async function fetchAhrefsRankTrackerProjects(
  apiKey: string,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<AdapterResult<AhrefsCallResult<ReadonlyArray<AhrefsRankTrackerProject>>>> {
  const result = await ahrefsGet<AhrefsRankTrackerProjectsRaw>(
    apiKey,
    "/keyword-list",
    { output: "json" },
    { ...ctx, endpoint: "/keyword-list" },
    { isFreeEndpoint: true }
  );
  if (!result.success) return result;
  const raw = result.data.data;
  const list = raw.project_list ?? raw.projects ?? [];
  const projects: AhrefsRankTrackerProject[] = [];
  for (const entry of list) {
    const id = entry.project_id ?? entry.id;
    if (typeof id !== "number" || !Number.isFinite(id)) continue;
    // Defensive fallback: if Ahrefs returns a project without a recognized
    // device value, default to "desktop" so the UI dropdown always renders
    // the entry rather than dropping it. The widget config UI lets the
    // customer override per-project before calling /competitors-stats.
    const device =
      entry.device === "desktop" || entry.device === "mobile"
        ? entry.device
        : "desktop";
    projects.push({
      projectId: id,
      name: entry.name ?? (entry as { title?: string }).title ?? `Project ${id}`,
      targetUrl: entry.target_url ?? (entry as { target?: string }).target ?? "",
      device,
      country: entry.country ?? "us",
    });
  }
  return adapterOk({ data: projects, cost: result.data.cost });
}

// ============================================================================
// Story 20.2 — Keywords History (AC #5)
// ============================================================================

/**
 * Story 20.2 AC #5 — DEFAULT `select` for `/site-explorer/keywords-history`.
 *
 * 6 fields using the **2026 expanded ranking groups** per the Ahrefs
 * changelog. The legacy `top11_plus` field is intentionally NOT in the
 * default select — it overlaps with the 3 new buckets and would double-count.
 */
export const KEYWORDS_HISTORY_DEFAULT_SELECT: ReadonlyArray<string> = [
  "date",
  "top3",
  "top4_10",
  "top11_20",
  "top21_50",
  "top51_plus",
];

/** Max date-range span for adapter-driven keywords-history calls. 24 months. */
export const KEYWORDS_HISTORY_MAX_RANGE_MONTHS = 24;

export interface KeywordsHistoryParams {
  readonly target: string;
  readonly date_from: string; // YYYY-MM-DD
  readonly select: ReadonlyArray<string>;
  /** Default `"subdomains"` per docs (matches organic-keywords default). */
  readonly mode?: AhrefsOrganicKeywordsMode;
  readonly protocol?: "http" | "https" | "both";
  readonly country?: string;
  readonly date_to?: string; // YYYY-MM-DD
  readonly history_grouping?: "daily" | "weekly" | "monthly";
}

/**
 * `GET /v3/site-explorer/keywords-history` — time-series keyword distribution
 * across position buckets. Uses the 2026 granular `top11_20` / `top21_50` /
 * `top51_plus` ranking groups by default.
 *
 * Story 20.2 AC #5 — paid endpoint, but cheap (~50 units for a 24-month
 * monthly query because all 6 default fields are 1-unit each, hitting the
 * 50-unit minimum).
 */
export async function fetchAhrefsKeywordsHistory(
  apiKey: string,
  params: KeywordsHistoryParams,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<
  AdapterResult<
    AhrefsCallResult<{ readonly history?: ReadonlyArray<Record<string, unknown>> }>
  >
> {
  if (!params.target || params.target.length === 0) {
    throw new TypeError("Ahrefs /site-explorer/keywords-history: `target` is required.");
  }
  if (!params.date_from || !/^\d{4}-\d{2}-\d{2}$/.test(params.date_from)) {
    throw new TypeError(
      "Ahrefs /site-explorer/keywords-history: `date_from` must be in YYYY-MM-DD format."
    );
  }
  if (params.date_to && !/^\d{4}-\d{2}-\d{2}$/.test(params.date_to)) {
    throw new TypeError(
      "Ahrefs /site-explorer/keywords-history: `date_to` must be in YYYY-MM-DD format."
    );
  }
  if (params.date_to) {
    const from = Date.parse(params.date_from);
    const to = Date.parse(params.date_to);
    if (Number.isFinite(from) && Number.isFinite(to)) {
      if (from > to) {
        throw new TypeError(
          "Ahrefs /site-explorer/keywords-history: `date_from` must precede `date_to`."
        );
      }
      const monthsSpan = (to - from) / (30.44 * 24 * 3600 * 1000);
      if (monthsSpan > KEYWORDS_HISTORY_MAX_RANGE_MONTHS) {
        throw new TypeError(
          `Ahrefs /site-explorer/keywords-history: date range exceeds ${KEYWORDS_HISTORY_MAX_RANGE_MONTHS} months — adapter caps to keep month-by-month unit costs predictable.`
        );
      }
    }
  }
  assertSelectNonEmpty(params.select, "/site-explorer/keywords-history");

  const wireParams: Record<string, string> = {
    target: params.target,
    date_from: params.date_from,
    select: params.select.join(","),
    mode: params.mode ?? "subdomains",
    protocol: params.protocol ?? "both",
    history_grouping: params.history_grouping ?? "monthly",
    output: "json",
  };
  if (params.country) wireParams.country = params.country.toLowerCase();
  if (params.date_to) wireParams.date_to = params.date_to;

  return fetchAhrefsEndpoint(
    apiKey,
    "keywords-history",
    wireParams,
    { ...ctx, target: params.target }
  );
}

// ============================================================================
// Story 20.2a — Metrics History (AC #2)
// ============================================================================

/**
 * Story 20.2a AC #2 — DEFAULT `select` for `/site-explorer/metrics-history`.
 *
 * 5 columns matching the Ahrefs API documented schema for this endpoint:
 *   - `date` (row marker, 0 units — date stamp per row)
 *   - `org_cost`     (10 units, USD cents — converted to dollars by transformer)
 *   - `org_traffic`  (10 units, monthly organic traffic estimate)
 *   - `paid_cost`    (10 units, USD cents)
 *   - `paid_traffic` (10 units, monthly paid traffic estimate)
 *
 * Verified against [docs.ahrefs.com/api/reference/site-explorer/get-metrics-history]
 * on 2026-05-03. The endpoint does NOT expose `domain_rating`, `org_keywords`,
 * or `paid_keywords` — those live on the `/metrics` snapshot endpoint or on
 * the dedicated `/domain-rating-history` endpoint (out of scope for this
 * story). Adding them to this `select` would 400 at runtime.
 */
export const METRICS_HISTORY_DEFAULT_SELECT: ReadonlyArray<string> = [
  "date",
  "org_cost",
  "org_traffic",
  "paid_cost",
  "paid_traffic",
];

/** Max date-range span for adapter-driven metrics-history calls. 24 months. */
export const METRICS_HISTORY_MAX_RANGE_MONTHS = 24;

export interface MetricsHistoryParams {
  readonly target: string;
  /** YYYY-MM-DD */
  readonly date_from: string;
  readonly select: ReadonlyArray<string>;
  /**
   * `mode` accepted values per [docs.ahrefs.com/api/reference/site-explorer/get-metrics-history]:
   * `"domain" | "subdomain" | "prefix" | "exact_url" | "subdomains"`. Default
   * is `"subdomains"` (plural).
   *
   * We re-use the Story 20.2 `AhrefsOrganicKeywordsMode` union (5 values
   * including `"subdomains"` plural) — it's a superset of Story 20.1's
   * canonical `AhrefsMode` (4 values, missing `"subdomains"` plural which is
   * the Ahrefs API default). Spec AC #2 declared the type as `AhrefsMode`,
   * but we widen it here so the helper accepts the actual API default
   * without requiring a Story 20.1 normalize.ts type widening (out of scope
   * for this data-layer story).
   */
  readonly mode?: AhrefsOrganicKeywordsMode;
  /** Default `"both"` per docs. */
  readonly protocol?: "http" | "https" | "both";
  /** Optional ISO 3166-1 alpha-2 lowercase country narrowing. */
  readonly country?: string;
  /** YYYY-MM-DD (must be ≥ date_from). */
  readonly date_to?: string;
  readonly history_grouping?: "daily" | "weekly" | "monthly";
  readonly volume_mode?: "monthly" | "average";
}

/**
 * `GET /v3/site-explorer/metrics-history` — daily/weekly/monthly time-series
 * of organic + paid traffic and their dollar-value equivalents per
 * [docs.ahrefs.com/api/reference/site-explorer/get-metrics-history].
 *
 * **Cost economics:** every metric field on this endpoint is 10-unit per row.
 *   - 12 monthly rows × 4 metric fields × 10 units = 480 units
 *   - 24 monthly rows × 4 metric fields × 10 units = 960 units
 *   - 90 daily rows × 4 metric fields × 10 units = 3,600 units
 * The default 12-month/monthly query (480 units) is what the autonomous sync
 * in `ahrefsAdapter.fetchMetrics` calls per Story 20.2a AC #5.
 *
 * **Response shape:** `{ "metrics": [ { date, org_cost, org_traffic, paid_cost, paid_traffic } ] }`.
 * Note: this endpoint uses `metrics` as the response array key — Ahrefs is
 * inconsistent across endpoints (`/keywords-history` uses `history`).
 */
export async function fetchAhrefsMetricsHistory(
  apiKey: string,
  params: MetricsHistoryParams,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<
  AdapterResult<
    AhrefsCallResult<{ readonly metrics?: ReadonlyArray<Record<string, unknown>> }>
  >
> {
  if (!params.target || params.target.length === 0) {
    throw new TypeError("Ahrefs /site-explorer/metrics-history: `target` is required.");
  }
  if (!params.date_from || !/^\d{4}-\d{2}-\d{2}$/.test(params.date_from)) {
    throw new TypeError(
      "Ahrefs /site-explorer/metrics-history: `date_from` must be in YYYY-MM-DD format."
    );
  }
  if (params.date_to && !/^\d{4}-\d{2}-\d{2}$/.test(params.date_to)) {
    throw new TypeError(
      "Ahrefs /site-explorer/metrics-history: `date_to` must be in YYYY-MM-DD format."
    );
  }
  if (params.date_to) {
    const from = Date.parse(params.date_from);
    const to = Date.parse(params.date_to);
    if (Number.isFinite(from) && Number.isFinite(to)) {
      if (from > to) {
        throw new TypeError(
          "Ahrefs /site-explorer/metrics-history: `date_from` must precede `date_to`."
        );
      }
      const monthsSpan = (to - from) / (30.44 * 24 * 3600 * 1000);
      if (monthsSpan > METRICS_HISTORY_MAX_RANGE_MONTHS) {
        throw new TypeError(
          `Ahrefs /site-explorer/metrics-history: date range exceeds ${METRICS_HISTORY_MAX_RANGE_MONTHS} months — adapter caps to keep month-by-month unit costs predictable.`
        );
      }
    }
  }
  if (params.country && !/^[a-z]{2}$/i.test(params.country)) {
    throw new TypeError(
      "Ahrefs /site-explorer/metrics-history: `country` must be a 2-letter ISO 3166-1 alpha-2 code (e.g. \"us\")."
    );
  }
  assertSelectNonEmpty(params.select, "/site-explorer/metrics-history");

  const wireParams: Record<string, string> = {
    target: params.target,
    date_from: params.date_from,
    select: params.select.join(","),
    mode: params.mode ?? "subdomains",
    protocol: params.protocol ?? "both",
    history_grouping: params.history_grouping ?? "monthly",
    output: "json",
  };
  if (params.country) wireParams.country = params.country.toLowerCase();
  if (params.date_to) wireParams.date_to = params.date_to;
  if (params.volume_mode) wireParams.volume_mode = params.volume_mode;

  return fetchAhrefsEndpoint(
    apiKey,
    "metrics-history",
    wireParams,
    { ...ctx, target: params.target }
  );
}

// ============================================================================
// Story 20.2a — Metrics by Country (AC #3)
// ============================================================================

/**
 * Story 20.2a AC #3 — DEFAULT `select` for `/site-explorer/metrics-by-country`.
 *
 * 9 columns matching the Ahrefs API documented schema:
 *   - `country`            (ISO 3166-1 alpha-2, dimension — 0 units)
 *   - `org_cost`           (10 units, USD cents → dollars)
 *   - `org_keywords`       (1 unit)
 *   - `org_keywords_1_3`   (1 unit, keywords ranking positions 1-3)
 *   - `org_traffic`        (10 units, monthly organic traffic)
 *   - `paid_cost`          (10 units, USD cents → dollars)
 *   - `paid_keywords`      (1 unit)
 *   - `paid_pages`         (1 unit)
 *   - `paid_traffic`       (10 units, monthly paid traffic)
 *
 * Verified against [docs.ahrefs.com/api/reference/site-explorer/get-metrics-by-country]
 * on 2026-05-03. The endpoint does NOT support `limit` or `order_by` parameters —
 * Ahrefs returns one row per country it has data for and the response is
 * naturally bounded (~250 ISO codes max).
 */
export const METRICS_BY_COUNTRY_DEFAULT_SELECT: ReadonlyArray<string> = [
  "country",
  "org_cost",
  "org_keywords",
  "org_keywords_1_3",
  "org_traffic",
  "paid_cost",
  "paid_keywords",
  "paid_pages",
  "paid_traffic",
];

export interface MetricsByCountryParams {
  readonly target: string;
  /** YYYY-MM-DD — required (snapshot date). */
  readonly date: string;
  readonly select: ReadonlyArray<string>;
  /**
   * Same widening rationale as `MetricsHistoryParams.mode` — accepts the
   * `"subdomains"` plural value documented as the API default for site-explorer
   * endpoints. See `MetricsHistoryParams.mode` for the full explanation.
   */
  readonly mode?: AhrefsOrganicKeywordsMode;
  /** Default `"both"` per docs. */
  readonly protocol?: "http" | "https" | "both";
  /** Optional ISO 3166-1 alpha-2 lowercase country filter. */
  readonly country?: string;
  readonly volume_mode?: "monthly" | "average";
}

/**
 * `GET /v3/site-explorer/metrics-by-country` — per-country breakdown of organic
 * + paid traffic and keyword counts at a snapshot `date` per
 * [docs.ahrefs.com/api/reference/site-explorer/get-metrics-by-country].
 *
 * **Cost economics:**
 *   - Per-row cost = 4 × 10-unit fields + 5 × 1-unit fields = 45 units, but
 *     subject to the 50-unit per-call floor.
 *   - 250-country result ≈ `250 × 45 = 11,250 units` — far above the floor.
 *   - Use the proxy-route 4h cache TTL aggressively to amortize this cost
 *     across multiple widget renders within a 4h window.
 *
 * **Response shape:** `{ "metrics": [ { country, org_cost, org_keywords, ... } ] }`.
 * Both `/metrics-history` and `/metrics-by-country` use `metrics` as the
 * response array key.
 *
 * **No `limit` / `order_by`:** Ahrefs does not document either parameter on
 * this endpoint. The helper does not pass them. If a future caller needs a
 * top-N filter they should sort/slice client-side after the response.
 */
export async function fetchAhrefsMetricsByCountry(
  apiKey: string,
  params: MetricsByCountryParams,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<
  AdapterResult<
    AhrefsCallResult<{ readonly metrics?: ReadonlyArray<Record<string, unknown>> }>
  >
> {
  if (!params.target || params.target.length === 0) {
    throw new TypeError("Ahrefs /site-explorer/metrics-by-country: `target` is required.");
  }
  if (!params.date || !/^\d{4}-\d{2}-\d{2}$/.test(params.date)) {
    throw new TypeError(
      "Ahrefs /site-explorer/metrics-by-country: `date` must be in YYYY-MM-DD format."
    );
  }
  if (params.country && !/^[a-z]{2}$/i.test(params.country)) {
    throw new TypeError(
      "Ahrefs /site-explorer/metrics-by-country: `country` must be a 2-letter ISO 3166-1 alpha-2 code (e.g. \"us\")."
    );
  }
  assertSelectNonEmpty(params.select, "/site-explorer/metrics-by-country");

  const wireParams: Record<string, string> = {
    target: params.target,
    date: params.date,
    select: params.select.join(","),
    mode: params.mode ?? "subdomains",
    protocol: params.protocol ?? "both",
    output: "json",
  };
  if (params.country) wireParams.country = params.country.toLowerCase();
  if (params.volume_mode) wireParams.volume_mode = params.volume_mode;

  return fetchAhrefsEndpoint(
    apiKey,
    "metrics-by-country",
    wireParams,
    { ...ctx, target: params.target }
  );
}

// ============================================================================
// Story 20.2c — Rank Tracker per-keyword `/overview` (FREE)
// ============================================================================

/**
 * Story 20.2c AC #2 — DEFAULT `select` for `/rank-tracker/overview`.
 *
 * Ships every documented response field (verified against
 * [docs.ahrefs.com/api/reference/rank-tracker/get-overview] on 2026-05-03).
 * Endpoint is FREE — no economic reason to subset; agencies expect every
 * column the Ahrefs Rank Tracker UI surfaces.
 *
 * Organized by category for readability — same grouping pattern as Story
 * 20.2b's widened `AHREFS_BATCH_ANALYSIS_DEFAULT_SELECT`.
 */
export const RANK_TRACKER_OVERVIEW_DEFAULT_SELECT: ReadonlyArray<string> = [
  // Position & ranking
  "best_position_has_thumbnail",
  "best_position_has_thumbnail_previous",
  "best_position_has_video_preview",
  "best_position_has_video_preview_previous",
  "best_position_kind",
  "best_position_kind_previous",
  "position",
  "position_prev",
  "position_diff",
  "is_main_position",
  "is_main_position_prev",
  "target_positions_count",
  // Keyword & search data
  "keyword",
  "keyword_prev",
  "keyword_difficulty",
  "keyword_has_data",
  "keyword_is_frozen",
  "keyword_words",
  "keyword_words_prev",
  "volume",
  "volume_desktop_pct",
  "volume_mobile_pct",
  "clicks",
  "clicks_per_search",
  "cost_per_click",
  "traffic",
  "traffic_prev",
  "traffic_diff",
  "parent_topic",
  "serp_features",
  "serp_features_prev",
  // Location & language
  "country",
  "country_prev",
  "location",
  "location_prev",
  "language",
  "language_prev",
  // Search type distribution
  "search_type_web",
  "search_type_image",
  "search_type_news",
  "search_type_video",
  // User intent flags
  "is_branded",
  "is_commercial",
  "is_informational",
  "is_local",
  "is_navigational",
  "is_transactional",
  // Metadata
  "created_at",
  "serp_updated",
  "serp_updated_prev",
  "tags",
  "tags_prev",
  "url",
  "url_prev",
];

/** Hard cap per Ahrefs docs — also the default. */
export const RANK_TRACKER_OVERVIEW_DEFAULT_LIMIT = 1000;

export interface RankTrackerOverviewParams {
  readonly project_id: number;
  readonly date: string; // YYYY-MM-DD
  /** Required — Ahrefs returns 400 without it. NO `"both"` option. */
  readonly device: "desktop" | "mobile";
  readonly select: ReadonlyArray<string>;
  /** YYYY-MM-DD — populates `*_diff` / `*_prev` fields. */
  readonly date_compared?: string;
  /** 1-1000; default 1000 (Ahrefs hard cap, endpoint is FREE). */
  readonly limit?: number;
  /** Ahrefs `order_by` expression (e.g. "position:asc"). */
  readonly order_by?: string;
  /** Ahrefs `where` filter expression (e.g. "tags=eq.branded"). */
  readonly where?: string;
  readonly volume_mode?: "monthly" | "average";
}

/**
 * `GET /v3/rank-tracker/overview` — per-keyword list of every tracked keyword's
 * current position, position change, traffic, search intent flags, SERP
 * features, search-type distribution, and tag classification.
 *
 * Story 20.2c AC #2 — **FREE endpoint, zero API units** per
 * [docs.ahrefs.com/api/reference/rank-tracker/get-overview]. The helper marks
 * `cacheStatus: "free"` so the cost rollup short-circuits to zero regardless
 * of `x-api-cache` header.
 *
 * `device` is mandatory and the helper does NOT default it (Rank Tracker
 * projects are configured per-device with no universal "both" mode — same
 * invariant as `/competitors-stats`).
 *
 * **Tag filtering uses `where`, not a `tags` query param.** The earlier R0
 * draft of this story listed `tags?: ReadonlyArray<string>` — that was a
 * documentation error. Per Ahrefs docs, `tags` is a RESPONSE field; tag
 * filtering goes through the `where` filter expression grammar
 * (e.g. `where=tags=eq.branded`).
 */
export async function fetchAhrefsRankTrackerOverview(
  apiKey: string,
  params: RankTrackerOverviewParams,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<
  AdapterResult<
    AhrefsCallResult<{ readonly keywords?: ReadonlyArray<Record<string, unknown>> }>
  >
> {
  if (typeof params.project_id !== "number" || !Number.isFinite(params.project_id)) {
    throw new TypeError(
      "Ahrefs /rank-tracker/overview: `project_id` is required (numeric)."
    );
  }
  if (!params.date || !/^\d{4}-\d{2}-\d{2}$/.test(params.date)) {
    throw new TypeError(
      "Ahrefs /rank-tracker/overview: `date` must be in YYYY-MM-DD format."
    );
  }
  if (params.device !== "desktop" && params.device !== "mobile") {
    throw new TypeError(
      "Rank Tracker requires explicit device scope (desktop | mobile) — Ahrefs does not support a unified 'both' device mode."
    );
  }
  if (
    params.date_compared !== undefined &&
    !/^\d{4}-\d{2}-\d{2}$/.test(params.date_compared)
  ) {
    throw new TypeError(
      "Ahrefs /rank-tracker/overview: `date_compared` must be in YYYY-MM-DD format."
    );
  }
  assertSelectNonEmpty(params.select, "/rank-tracker/overview");

  // Defensive clamp to the Ahrefs hard cap (1000). Mirrors the
  // `fetchAhrefsOrganicKeywords` pattern. The proxy route's Zod schema
  // also caps via `.max(...)`, but direct callers (autonomous sync, tests,
  // future internal flows) should hit the same ceiling.
  // `limit: 0` falls through to the default — a zero-row request burns a
  // call for no data and is almost certainly a widget-config bug.
  const requestedLimit = params.limit && params.limit > 0
    ? params.limit
    : RANK_TRACKER_OVERVIEW_DEFAULT_LIMIT;
  const limit = Math.min(requestedLimit, RANK_TRACKER_OVERVIEW_DEFAULT_LIMIT);
  const wireParams: Record<string, string> = {
    project_id: String(params.project_id),
    date: params.date,
    device: params.device,
    select: params.select.join(","),
    limit: String(limit),
    output: "json",
  };
  if (params.date_compared) wireParams.date_compared = params.date_compared;
  if (params.order_by) wireParams.order_by = params.order_by;
  if (params.where) wireParams.where = params.where;
  if (params.volume_mode) wireParams.volume_mode = params.volume_mode;

  return fetchAhrefsEndpoint(
    apiKey,
    "rank-tracker-overview",
    wireParams,
    ctx,
    { isFreeEndpoint: true }
  );
}

// ============================================================================
// Story 20.2d — Site Audit family (4 endpoints)
// ============================================================================

/**
 * Story 20.2d AC #2 — DEFAULT `select` for `/site-audit/projects`.
 *
 * 12 wire-format fields — all FREE per [docs.ahrefs.com/api/reference/site-audit/get-projects]
 * (verified 2026-05-05, "Requests to this endpoint are free and do not consume
 * any API units"). Same FREE-endpoint posture as `/rank-tracker/competitors-stats`
 * (Story 20.2) and `/rank-tracker/overview` (Story 20.2c) — ship every documented
 * field because there's no economic reason to subset.
 */
export const SITE_AUDIT_PROJECTS_DEFAULT_SELECT: ReadonlyArray<string> = [
  "project_id",
  "project_name",
  "target_protocol",
  "target_url",
  "target_mode",
  "date",
  "status",
  "health_score",
  "urls_with_errors",
  "urls_with_warnings",
  "urls_with_notices",
  "total",
];

/**
 * Story 20.2d AC #3 — DEFAULT `select` for `/site-audit/issues`.
 *
 * 11 wire-format fields. The endpoint cost is a fixed 50 units per request
 * (NOT row-scaled per docs.ahrefs.com/api/reference/site-audit/get-issues) so
 * opt-in field selection saves nothing economically — same "ship everything"
 * pattern as the FREE endpoints.
 *
 * Notable enums (preserved verbatim by the transformer):
 *  - `importance` ∈ `Error | Warning | Notice` (capitalized — wire-format).
 *  - `category` ∈ 15 documented values (Internal pages, Indexability, Links,
 *    Redirects, Content, Social tags, Duplicates, Localization, Usability and
 *    performance, Images, JavaScript, CSS, Sitemaps, External pages, Other).
 *
 * The 5 comparison fields (`change`/`added`/`new`/`removed`/`missing`) are
 * populated only when `date_compared` is supplied — without it Ahrefs returns
 * `null` for those columns; the transformer preserves the null so widgets can
 * render "no comparison data" empty states accurately.
 */
export const SITE_AUDIT_ISSUES_DEFAULT_SELECT: ReadonlyArray<string> = [
  "issue_id",
  "name",
  "importance",
  "category",
  "is_indexable",
  "crawled",
  "change",
  "added",
  "new",
  "removed",
  "missing",
];

export interface SiteAuditProjectsParams {
  /** Optional: filter by target URL. */
  readonly project_url?: string;
  /** Optional: filter by project name (substring match per Ahrefs docs). */
  readonly project_name?: string;
  /**
   * Optional UTC timestamp `YYYY-MM-DDThh:mm:ss` (or shorter `YYYY-MM-DD` —
   * Ahrefs tolerates both). Defaults to most-recent crawl when omitted.
   */
  readonly date?: string;
  /** Optional: single-project filter. */
  readonly project_id?: number;
  /** Optional but recommended; defaults to SITE_AUDIT_PROJECTS_DEFAULT_SELECT. */
  readonly select?: ReadonlyArray<string>;
}

export interface AhrefsSiteAuditProjectRow {
  readonly project_id: string;
  readonly project_name: string;
  readonly target_protocol: string; // "http" | "https"
  readonly target_url: string;
  readonly target_mode: string; // "domain" | "subdomain" | "prefix" | "exact_url"
  readonly date: string | null; // ISO timestamp of latest crawl
  readonly status: string | null; // crawl status string
  readonly health_score: number | null; // 0-100
  readonly urls_with_errors: number | null;
  readonly urls_with_warnings: number | null;
  readonly urls_with_notices: number | null;
  readonly total: number | null; // total URLs crawled
}

/**
 * Story 20.2d AC #2 — accepts both `YYYY-MM-DD` and `YYYY-MM-DDThh:mm:ss`.
 * Ahrefs documents the full timestamp shape; in practice the API accepts the
 * date-only shorthand. The Zod-side route validator normalizes by appending
 * `T00:00:00` when only the date is supplied; the helper passes through
 * verbatim so adapter-driven callers (which already know the right shape)
 * aren't forced through an extra transformation.
 */
const SITE_AUDIT_DATE_REGEX = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?$/;

/**
 * `GET /v3/site-audit/projects` — customer's full Site Audit project list.
 *
 * Story 20.2d AC #2 — **FREE endpoint, zero API units** per
 * [docs.ahrefs.com/api/reference/site-audit/get-projects] (verified 2026-05-05).
 * Marks `cacheStatus: "free"` so the cost rollup short-circuits to zero
 * regardless of the wire `x-api-cache` header.
 *
 * Response envelope: `{ healthscores?: AhrefsSiteAuditProjectRow[] }` — note
 * the singular noun is `healthscore` per Ahrefs's wire format (not "projects").
 * No required params; calling with no params returns the customer's full
 * project list at most-recent crawl date.
 */
export async function fetchAhrefsSiteAuditProjects(
  apiKey: string,
  params: SiteAuditProjectsParams,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<
  AdapterResult<
    AhrefsCallResult<{ readonly healthscores?: ReadonlyArray<AhrefsSiteAuditProjectRow> }>
  >
> {
  if (params.date !== undefined && !SITE_AUDIT_DATE_REGEX.test(params.date)) {
    throw new TypeError(
      "Ahrefs /site-audit/projects: `date` must be in YYYY-MM-DD or YYYY-MM-DDThh:mm:ss format."
    );
  }
  if (
    params.project_id !== undefined &&
    (typeof params.project_id !== "number" || !Number.isFinite(params.project_id))
  ) {
    throw new TypeError(
      "Ahrefs /site-audit/projects: `project_id`, when supplied, must be a finite number."
    );
  }
  const select = params.select ?? SITE_AUDIT_PROJECTS_DEFAULT_SELECT;
  assertSelectNonEmpty(select, "/site-audit/projects");

  const wireParams: Record<string, string> = {
    select: select.join(","),
    output: "json",
  };
  if (params.project_url) wireParams.project_url = params.project_url;
  if (params.project_name) wireParams.project_name = params.project_name;
  if (params.date) wireParams.date = params.date;
  if (params.project_id !== undefined) wireParams.project_id = String(params.project_id);

  return fetchAhrefsEndpoint(
    apiKey,
    "site-audit-projects",
    wireParams,
    ctx,
    { isFreeEndpoint: true }
  );
}

export interface SiteAuditIssuesParams {
  /** REQUIRED — TypeError synchronously on missing/non-numeric. */
  readonly project_id: number;
  /** Optional UTC timestamp; defaults to most-recent crawl. */
  readonly date?: string;
  /**
   * Optional UTC timestamp; populates the 5 comparison fields
   * (`change`/`added`/`new`/`removed`/`missing`) which return `null` without it.
   */
  readonly date_compared?: string;
  /** Optional; defaults to SITE_AUDIT_ISSUES_DEFAULT_SELECT (all 11 fields). */
  readonly select?: ReadonlyArray<string>;
  /** Optional, default 1000 (Ahrefs hard cap). */
  readonly limit?: number;
  /** Optional, e.g. "crawled:desc". */
  readonly order_by?: string;
  /** Optional Ahrefs filter expression. */
  readonly where?: string;
}

/**
 * Story 20.2d — Ahrefs `/site-audit/issues` and `/site-audit/page-explorer`
 * hard cap (per docs.ahrefs.com verification 2026-05-05). Same defensive
 * clamp pattern as `/rank-tracker/overview` (Story 20.2c R1) — protects
 * adapter-direct callers (autonomous sync, internal flows, future tests)
 * from sending oversized `limit` values that would silently truncate at
 * the Ahrefs edge or burn unit budget on cap-bypassed responses.
 */
export const SITE_AUDIT_HARD_LIMIT = 1000;

/**
 * `GET /v3/site-audit/issues` — Site Audit issues catalogue for a single project.
 *
 * Story 20.2d AC #3 — paid endpoint with **fixed 50 units/request** cost
 * (NOT row-scaled per docs.ahrefs.com/api/reference/site-audit/get-issues,
 * verified 2026-05-05). The fixed cost makes opt-in field subsetting
 * economically pointless — `SITE_AUDIT_ISSUES_DEFAULT_SELECT` ships every
 * documented field by default.
 *
 * Response envelope: `{ issues?: ReadonlyArray<...> }`.
 */
export async function fetchAhrefsSiteAuditIssues(
  apiKey: string,
  params: SiteAuditIssuesParams,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<
  AdapterResult<
    AhrefsCallResult<{ readonly issues?: ReadonlyArray<Record<string, unknown>> }>
  >
> {
  if (typeof params.project_id !== "number" || !Number.isFinite(params.project_id)) {
    throw new TypeError(
      "Ahrefs /site-audit/issues: `project_id` is required (finite number)."
    );
  }
  if (params.date !== undefined && !SITE_AUDIT_DATE_REGEX.test(params.date)) {
    throw new TypeError(
      "Ahrefs /site-audit/issues: `date` must be in YYYY-MM-DD or YYYY-MM-DDThh:mm:ss format."
    );
  }
  if (
    params.date_compared !== undefined &&
    !SITE_AUDIT_DATE_REGEX.test(params.date_compared)
  ) {
    throw new TypeError(
      "Ahrefs /site-audit/issues: `date_compared` must be in YYYY-MM-DD or YYYY-MM-DDThh:mm:ss format."
    );
  }
  const select = params.select ?? SITE_AUDIT_ISSUES_DEFAULT_SELECT;
  assertSelectNonEmpty(select, "/site-audit/issues");

  const wireParams: Record<string, string> = {
    project_id: String(params.project_id),
    select: select.join(","),
    output: "json",
  };
  if (params.date) wireParams.date = params.date;
  if (params.date_compared) wireParams.date_compared = params.date_compared;
  if (params.limit !== undefined && params.limit > 0) {
    // Defensive clamp to the Ahrefs hard cap (1000). `limit: 0` falls through
    // (Ahrefs default) — a zero-row request is almost certainly a widget-config
    // bug. Mirrors the Story 20.2c `/rank-tracker/overview` clamp pattern.
    wireParams.limit = String(Math.min(params.limit, SITE_AUDIT_HARD_LIMIT));
  }
  if (params.order_by) wireParams.order_by = params.order_by;
  if (params.where) wireParams.where = params.where;

  return fetchAhrefsEndpoint(apiKey, "site-audit-issues", wireParams, ctx);
}

export interface SiteAuditPageExplorerParams {
  /** REQUIRED. */
  readonly project_id: number;
  /**
   * REQUIRED non-empty per docs — the 608-field response surface is too large
   * to ship a sane default-select. Caller MUST explicitly pick fields.
   */
  readonly select: ReadonlyArray<string>;
  readonly date?: string;
  readonly date_compared?: string;
  /** 1-1000 (Ahrefs hard cap). */
  readonly limit?: number;
  readonly offset?: number;
  readonly order_by?: string;
  readonly where?: string;
  /** Optional per-call timeout override (Ahrefs query expression timeout). */
  readonly timeout?: number;
}

/**
 * `GET /v3/site-audit/page-explorer` — per-URL Site Audit dataset.
 *
 * Story 20.2d AC #4 — paid endpoint with **fixed 50 units/request** cost.
 * The 608-field SDK surface (per `ahrefs-python/src/ahrefs/types/_generated.py`
 * lines 3815-6084; public docs list 500+ fields) is intentionally NOT
 * catalogued at field-level — Story 20.3 will surface ~12 priority fields
 * (`http_code`, `title`, `meta_description`, `canonical`, `h1`, `compliant`,
 * `depth`, `content_length`, `loading_time`, `internal_links`, `external_links`,
 * `redirect`) when the widget config UI lands.
 *
 * Response envelope: `{ pages?: ReadonlyArray<Record<string, unknown>> }`
 * (loosely typed because the row shape is widget-config-driven).
 */
export async function fetchAhrefsSiteAuditPageExplorer(
  apiKey: string,
  params: SiteAuditPageExplorerParams,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<
  AdapterResult<
    AhrefsCallResult<{ readonly pages?: ReadonlyArray<Record<string, unknown>> }>
  >
> {
  if (typeof params.project_id !== "number" || !Number.isFinite(params.project_id)) {
    throw new TypeError(
      "Ahrefs /site-audit/page-explorer: `project_id` is required (finite number)."
    );
  }
  assertSelectNonEmpty(params.select, "/site-audit/page-explorer");
  if (params.date !== undefined && !SITE_AUDIT_DATE_REGEX.test(params.date)) {
    throw new TypeError(
      "Ahrefs /site-audit/page-explorer: `date` must be in YYYY-MM-DD or YYYY-MM-DDThh:mm:ss format."
    );
  }
  if (
    params.date_compared !== undefined &&
    !SITE_AUDIT_DATE_REGEX.test(params.date_compared)
  ) {
    throw new TypeError(
      "Ahrefs /site-audit/page-explorer: `date_compared` must be in YYYY-MM-DD or YYYY-MM-DDThh:mm:ss format."
    );
  }

  const wireParams: Record<string, string> = {
    project_id: String(params.project_id),
    select: params.select.join(","),
    output: "json",
  };
  if (params.date) wireParams.date = params.date;
  if (params.date_compared) wireParams.date_compared = params.date_compared;
  if (params.limit !== undefined && params.limit > 0) {
    wireParams.limit = String(Math.min(params.limit, SITE_AUDIT_HARD_LIMIT));
  }
  if (params.offset !== undefined && params.offset >= 0) {
    wireParams.offset = String(params.offset);
  }
  if (params.order_by) wireParams.order_by = params.order_by;
  if (params.where) wireParams.where = params.where;
  if (params.timeout !== undefined) wireParams.timeout = String(params.timeout);

  return fetchAhrefsEndpoint(apiKey, "site-audit-page-explorer", wireParams, ctx);
}

export interface SiteAuditPageContentParams {
  /** REQUIRED. */
  readonly project_id: number;
  /** REQUIRED — the page URL whose content to retrieve. */
  readonly target_url: string;
  /**
   * REQUIRED non-empty — caller picks from 4 documented fields:
   * `["page_text"]`, `["raw_html"]`, `["rendered_html"]`, or all of them.
   */
  readonly select: ReadonlyArray<string>;
  readonly date?: string;
}

/**
 * `GET /v3/site-audit/page-content` — raw content extraction for a single page.
 *
 * Story 20.2d AC #5 — paid endpoint with **fixed 50 units/request** cost.
 * Returns extracted text + raw HTML + rendered HTML for one page; rarely-used
 * for dashboard surfaces but ships for completeness and future content-comparison
 * widgets (e.g. "show me the `<title>` content for these underperforming URLs").
 *
 * Response envelope: `{ "page-content"?: { crawl_datetime, page_text, raw_html,
 * rendered_html } }` — note the kebab-case key (`page-content`, not
 * `pageContent`) is Ahrefs's wire-format.
 *
 * No `MetricDefinition` rows are added for this endpoint — content extraction
 * is not a numeric metric source.
 */
export async function fetchAhrefsSiteAuditPageContent(
  apiKey: string,
  params: SiteAuditPageContentParams,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<
  AdapterResult<
    AhrefsCallResult<{
      readonly "page-content"?: {
        readonly crawl_datetime?: string | null;
        readonly page_text?: string | null;
        readonly raw_html?: string | null;
        readonly rendered_html?: string | null;
      };
    }>
  >
> {
  if (typeof params.project_id !== "number" || !Number.isFinite(params.project_id)) {
    throw new TypeError(
      "Ahrefs /site-audit/page-content: `project_id` is required (finite number)."
    );
  }
  if (typeof params.target_url !== "string" || params.target_url.length === 0) {
    throw new TypeError(
      "Ahrefs /site-audit/page-content: `target_url` is required (non-empty string)."
    );
  }
  assertSelectNonEmpty(params.select, "/site-audit/page-content");
  if (params.date !== undefined && !SITE_AUDIT_DATE_REGEX.test(params.date)) {
    throw new TypeError(
      "Ahrefs /site-audit/page-content: `date` must be in YYYY-MM-DD or YYYY-MM-DDThh:mm:ss format."
    );
  }

  const wireParams: Record<string, string> = {
    project_id: String(params.project_id),
    target_url: params.target_url,
    select: params.select.join(","),
    output: "json",
  };
  if (params.date) wireParams.date = params.date;

  return fetchAhrefsEndpoint(apiKey, "site-audit-page-content", wireParams, ctx);
}

// ============================================================================
// Story 20.2e — Brand Radar (8 GET endpoints, 9 _reportType discriminators)
// ============================================================================
//
// Per [docs.ahrefs.com/api/reference/brand-radar] verified 2026-05-05, Brand
// Radar exposes 18 endpoints (9 GET + 9 POST). Story 20.2e ships the 8 GET
// visibility helpers; POST variants are deferred (heavy-filter use cases not
// yet surfaced in widgets). The 8 endpoints map to 9 `_reportType` literals
// because `/sov-overview` and `/sov-history` are distinct surfaces with
// different response shapes.
//
// **Subscription posture.** Brand Radar is a paid add-on layered on top of
// any paid Ahrefs subscription ($199/month per AI platform, $699/month for
// all 7 platforms; custom-prompt tracking $50-$250/month tiered). NOT bundled
// with Enterprise. The `discoverEndpoints` framework probes a single FREE
// `/sov-overview` call (with `prompts=custom`) to detect the gate; on 403 +
// BR-flavored message, all 8 BR endpoints flip to `subscription_required`.
//
// **Cost mechanics.** `prompts=custom` makes the call FREE per docs (only the
// customer's custom-prompt portion is billed; with no custom prompts the call
// is zero-units). `prompts=ahrefs` follows standard pricing. Per-field
// surcharges: `response` = 10u, `links` = 10u, `volume` = 10u (on
// `/ai-responses` and the cited-* endpoints). The proxy-route layer pre-flights
// the cost-rollup against `BRAND_RADAR_FIELD_COSTS` so opt-in paid fields don't
// silently exhaust the per-tenant 5M-units/day cap.
//
// **2026 deprecations.** `mentions` field on `/cited-domains` and
// `/cited-pages` was deprecated 2026-02-10 (replacement: `responses`). `volume`
// field on the same 2 endpoints was deprecated 2026-03-24 (no replacement).
// Both ship as `deprecated: true` MetricDefinitions and are NULLed at the
// transformer ingest layer so widgets never render stale values. Critically:
// `mentions` on `/mentions-history` + `/mentions-overview` is a LEGITIMATE
// count metric (NOT the deprecated cited-* field) — the metric-allowlist grep
// guardrail narrows from "literal `mentions` anywhere in source" to a
// context-aware substring check (per AC #5).

/**
 * The 7 documented AI clients accepted as `data_source` values per
 * [help.ahrefs.com/en/articles/11064852-about-brand-radar] verified 2026-05-05.
 *
 * `grok` is the most-recent addition (2026); the other 6 ship since the
 * Brand Radar API GA in 2025-09. If Ahrefs adds an 8th client mid-quarter,
 * this const requires a one-line update + a test bump (the
 * `metric-allowlist.test.ts` allowlist arms include this list verbatim).
 *
 * Wire-format (snake_case). Comma-separated when serialized into the GET
 * `data_source` query param. The route layer accepts the same set via Zod
 * `.refine`.
 */
export const BRAND_RADAR_ALL_DATA_SOURCES = [
  "google_ai_overviews",
  "google_ai_mode",
  "chatgpt",
  "gemini",
  "perplexity",
  "copilot",
  "grok",
] as const satisfies ReadonlyArray<string>;

export type BrandRadarDataSource = (typeof BRAND_RADAR_ALL_DATA_SOURCES)[number];

/**
 * Per-field unit surcharges on Brand Radar endpoints (verified 2026-05-05).
 *
 * Three fields cost 10 units each when included in the request `select`:
 *   - `response` — verbatim AI-generated answer text (on `/ai-responses`).
 *   - `links` — cited URLs array (on `/ai-responses`).
 *   - `volume` — search-volume estimate (on `/ai-responses` and the
 *     `/cited-*` endpoints; on cited-* it's deprecated and stripped at
 *     ingest, but Ahrefs still bills the call if the field appears in
 *     `select`, so the cost map covers it).
 *
 * Other fields are free. The proxy-route `checkAhrefsUnitBudget` pre-flight
 * uses this map to compute the expected cost from the requested `select` list
 * BEFORE the API call; when `prompts=custom` the cost is forced to 0.
 */
export const BRAND_RADAR_FIELD_COSTS = {
  response: 10,
  links: 10,
  volume: 10,
} as const satisfies Record<string, number>;

/**
 * Default `select` for `/brand-radar/ai-responses`. Excludes the 3 paid
 * 10u-each fields (`response`, `links`, `volume`) by default — widget config
 * opts-in via the future Story 20.3 widget UI. Ships the 4 free fields:
 *   - `country` (free) — ISO 3166-1 alpha-2 lowercase
 *   - `question` (free) — the underlying user query
 *   - `search_queries` (free, ChatGPT/Perplexity only) — array of refined queries
 *   - `tags` (free) — Ahrefs-assigned topical tags array
 */
export const BRAND_RADAR_AI_RESPONSES_DEFAULT_SELECT: ReadonlyArray<string> = [
  "country",
  "question",
  "search_queries",
  "tags",
];

/**
 * Default `select` for `/brand-radar/impressions-overview` AND
 * `/brand-radar/mentions-overview`. Both endpoints share the same 5-bucket
 * shape per the catalogue rows 194-205. None of the 6 fields carry a
 * per-field cost — ship all 6 by default.
 */
export const BRAND_RADAR_OVERVIEW_DEFAULT_SELECT: ReadonlyArray<string> = [
  "brand",
  "only_target_brand",
  "only_competitors_brands",
  "target_and_competitors_brands",
  "no_tracked_brands",
  "total",
];

/**
 * Default `select` for `/brand-radar/cited-domains`. Ships the 3 non-deprecated
 * fields. `mentions` (deprecated 2026-02-10) and `volume` (deprecated
 * 2026-03-24) are NOT in the default — widgets that explicitly request them
 * receive `null` mirrors at the transformer layer (per AC #5).
 */
export const BRAND_RADAR_CITED_DOMAINS_DEFAULT_SELECT: ReadonlyArray<string> = [
  "domain",
  "pages",
  "responses",
];

/**
 * Default `select` for `/brand-radar/cited-pages`. Same deprecation posture
 * as cited-domains — `mentions` + `volume` excluded. `responses` is the
 * canonical cited-pages count metric (replaces deprecated `mentions`).
 */
export const BRAND_RADAR_CITED_PAGES_DEFAULT_SELECT: ReadonlyArray<string> = [
  "url",
  "responses",
];

/**
 * Validate a `data_source` value array. Throws `TypeError` synchronously on
 * empty / typoed values — defensive against widget-config bugs that would
 * otherwise return 400 from Ahrefs and burn a unit-budget call.
 *
 * Accepts a `ReadonlyArray<BrandRadarDataSource>` at the type level, but
 * defends at runtime against callers (e.g. widget-config flows) bypassing
 * the compile-time guard with a typo'd literal.
 */
function assertBrandRadarDataSourceNonEmpty(
  // Code Review Pass 3 (P3-L6) — tightened from `ReadonlyArray<string> |
  // undefined` to `ReadonlyArray<string>` so future TypeScript callers are
  // forced to satisfy the type at compile time. Runtime defense kept (a
  // caller might still pass `undefined` via `as never` cast).
  dataSource: ReadonlyArray<string>,
  endpoint: string
): void {
  if (!Array.isArray(dataSource) || dataSource.length === 0) {
    throw new TypeError(
      `Ahrefs ${endpoint}: \`data_source\` must be a non-empty array — Brand Radar endpoints reject 400 without it. Pass BRAND_RADAR_ALL_DATA_SOURCES if the caller wants every AI client.`
    );
  }
  for (const value of dataSource) {
    if (
      !BRAND_RADAR_ALL_DATA_SOURCES.includes(value as BrandRadarDataSource)
    ) {
      // Code Review Pass 2 (L3) — sanitize echoed value to prevent
      // log/response injection if widget config ever forwards user input
      // here unsanitized. Cap length, restrict to alphanumeric + dash/underscore.
      const safeValue = String(value).slice(0, 32).replace(/[^\w-]/g, "?");
      throw new TypeError(
        `Ahrefs ${endpoint}: \`data_source\` value "${safeValue}" is not one of the 7 documented AI clients (${BRAND_RADAR_ALL_DATA_SOURCES.join(", ")}).`
      );
    }
  }
}

/**
 * YYYY-MM-DD regex shared by Brand Radar history endpoints (impressions +
 * mentions + sov-history) and the proxy-route layer's Zod schemas. Exported
 * (Code Review Pass 2, M7) so route-side and helper-side validators can
 * import a single source of truth and never drift.
 */
export const BRAND_RADAR_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

// ----------------------------------------------------------------------------
// Story 20.2e — /brand-radar/ai-responses
// ----------------------------------------------------------------------------

/**
 * Note on `output: "json"` across all 9 BR param interfaces (Code Review
 * Pass 3, P3-M7): the field is preserved for forward-compat with future
 * Ahrefs response formats (e.g. CSV, Parquet) that the public docs hint
 * at but don't surface today. No route Zod schema reads it; helpers always
 * default to `"json"`. Adapter-direct callers (autonomous sync, internal
 * flows) get the option without needing a future-stamp interface bump.
 */
export interface BrandRadarAiResponsesParams {
  /** REQUIRED non-empty per docs. */
  readonly select: ReadonlyArray<string>;
  /** REQUIRED non-empty per docs. Validated against BRAND_RADAR_ALL_DATA_SOURCES. */
  readonly data_source: ReadonlyArray<BrandRadarDataSource>;
  readonly where?: string;
  readonly limit?: number;
  /** YYYY-MM-DD snapshot date. */
  readonly date?: string;
  readonly country?: string;
  /** Default `"relevance"` per docs. */
  readonly order_by?: "relevance" | "volume";
  readonly report_id?: string;
  /**
   * `"ahrefs"` (paid) | `"custom"` (FREE — customer-uploaded prompts only).
   * Default per docs is "both" (no `prompts` param → returns both data sets,
   * billed per Ahrefs portion only). The helper does NOT default this — leaves
   * the choice to the caller.
   */
  readonly prompts?: "ahrefs" | "custom";
  readonly market?: string;
  readonly competitors?: string;
  readonly brand?: string;
  /** Default `"json"`. See note at top of BR section. */
  readonly output?: "json";
}

/**
 * `GET /v3/brand-radar/ai-responses` — verbatim AI answers / cited URLs /
 * topical tags per Brand Radar response surface.
 *
 * Story 20.2e — paid endpoint with **per-field cost surcharges**: `response`,
 * `links`, and `volume` cost 10 units each when included. Default-select
 * (4 fields) excludes the 3 paid fields; widget config opts-in.
 *
 * **FREE invariant:** when `prompts === "custom"` the call is FREE per docs
 * (returns customer-data portion only). The helper passes `isFreeEndpoint:
 * true` to the cost-rollup wrapper so the daily-unit budget short-circuits
 * to zero on these calls.
 */
export async function fetchAhrefsBrandRadarAiResponses(
  apiKey: string,
  params: BrandRadarAiResponsesParams,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<
  AdapterResult<
    AhrefsCallResult<{ readonly ai_responses?: ReadonlyArray<Record<string, unknown>> }>
  >
> {
  assertSelectNonEmpty(params.select, "/brand-radar/ai-responses");
  assertBrandRadarDataSourceNonEmpty(params.data_source, "/brand-radar/ai-responses");
  if (params.date !== undefined && !BRAND_RADAR_DATE_REGEX.test(params.date)) {
    throw new TypeError(
      "Ahrefs /brand-radar/ai-responses: `date` must be in YYYY-MM-DD format."
    );
  }
  if (params.country !== undefined && !/^[a-z]{2}$/i.test(params.country)) {
    throw new TypeError(
      "Ahrefs /brand-radar/ai-responses: `country` must be a 2-letter ISO 3166-1 alpha-2 code (e.g. \"us\")."
    );
  }

  const wireParams: Record<string, string> = {
    select: params.select.join(","),
    data_source: params.data_source.join(","),
    output: params.output ?? "json",
  };
  if (params.where) wireParams.where = params.where;
  if (params.limit !== undefined && params.limit > 0) {
    wireParams.limit = String(params.limit);
  }
  if (params.date) wireParams.date = params.date;
  if (params.country) wireParams.country = params.country.toLowerCase();
  if (params.order_by) wireParams.order_by = params.order_by;
  if (params.report_id) wireParams.report_id = params.report_id;
  if (params.prompts) wireParams.prompts = params.prompts;
  if (params.market) wireParams.market = params.market;
  if (params.competitors) wireParams.competitors = params.competitors;
  if (params.brand) wireParams.brand = params.brand;

  return fetchAhrefsEndpoint(
    apiKey,
    "brand-radar-ai-responses",
    wireParams,
    ctx,
    { isFreeEndpoint: params.prompts === "custom" }
  );
}

// ----------------------------------------------------------------------------
// Story 20.2e — /brand-radar/impressions-history + /brand-radar/mentions-history
// ----------------------------------------------------------------------------
//
// Both endpoints share an identical param skeleton + 2-field response shape:
//   { metrics: [{ date, impressions }] }  // or { date, mentions }
// `mentions` here is the legitimate count metric, NOT the deprecated cited-*
// field (per AC #5).

export interface BrandRadarImpressionsHistoryParams {
  /** REQUIRED non-empty array of AI-client wire literals. */
  readonly data_source: ReadonlyArray<BrandRadarDataSource>;
  /** REQUIRED YYYY-MM-DD start date. */
  readonly date_from: string;
  /** Optional YYYY-MM-DD end date. */
  readonly date_to?: string;
  readonly where?: string;
  readonly country?: string;
  readonly report_id?: string;
  readonly prompts?: "ahrefs" | "custom";
  readonly market?: string;
  readonly brands?: string;
  readonly output?: "json";
}

/** Shared interface for `/mentions-history` — same param shape as impressions-history. */
export type BrandRadarMentionsHistoryParams = BrandRadarImpressionsHistoryParams;

function buildBrandRadarHistoryWireParams(
  params: BrandRadarImpressionsHistoryParams,
  endpoint: string
): Record<string, string> {
  assertBrandRadarDataSourceNonEmpty(params.data_source, endpoint);
  if (!params.date_from || !BRAND_RADAR_DATE_REGEX.test(params.date_from)) {
    throw new TypeError(
      `Ahrefs ${endpoint}: \`date_from\` must be in YYYY-MM-DD format.`
    );
  }
  if (params.date_to && !BRAND_RADAR_DATE_REGEX.test(params.date_to)) {
    throw new TypeError(
      `Ahrefs ${endpoint}: \`date_to\` must be in YYYY-MM-DD format.`
    );
  }
  if (params.date_to) {
    const from = Date.parse(params.date_from);
    const to = Date.parse(params.date_to);
    if (Number.isFinite(from) && Number.isFinite(to) && from > to) {
      throw new TypeError(
        `Ahrefs ${endpoint}: \`date_from\` must precede \`date_to\`.`
      );
    }
  }
  if (params.country !== undefined && !/^[a-z]{2}$/i.test(params.country)) {
    throw new TypeError(
      `Ahrefs ${endpoint}: \`country\` must be a 2-letter ISO 3166-1 alpha-2 code (e.g. "us").`
    );
  }

  const wireParams: Record<string, string> = {
    data_source: params.data_source.join(","),
    date_from: params.date_from,
    output: params.output ?? "json",
  };
  if (params.date_to) wireParams.date_to = params.date_to;
  if (params.where) wireParams.where = params.where;
  if (params.country) wireParams.country = params.country.toLowerCase();
  if (params.report_id) wireParams.report_id = params.report_id;
  if (params.prompts) wireParams.prompts = params.prompts;
  if (params.market) wireParams.market = params.market;
  if (params.brands) wireParams.brands = params.brands;
  return wireParams;
}

/**
 * `GET /v3/brand-radar/impressions-history` — daily-cadence time-series of
 * brand impression counts across the requested AI clients. Response shape:
 * `{ metrics: [{ date, impressions }] }`. No `select` — shape is fixed.
 */
export async function fetchAhrefsBrandRadarImpressionsHistory(
  apiKey: string,
  params: BrandRadarImpressionsHistoryParams,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<
  AdapterResult<
    AhrefsCallResult<{ readonly metrics?: ReadonlyArray<Record<string, unknown>> }>
  >
> {
  const wireParams = buildBrandRadarHistoryWireParams(
    params,
    "/brand-radar/impressions-history"
  );
  return fetchAhrefsEndpoint(
    apiKey,
    "brand-radar-impressions-history",
    wireParams,
    ctx,
    { isFreeEndpoint: params.prompts === "custom" }
  );
}

/**
 * `GET /v3/brand-radar/mentions-history` — daily-cadence time-series of brand
 * mention counts. Response shape: `{ metrics: [{ date, mentions }] }` — note
 * the `mentions` field here is the legitimate count metric (NOT the deprecated
 * `cited-*` field).
 */
export async function fetchAhrefsBrandRadarMentionsHistory(
  apiKey: string,
  params: BrandRadarMentionsHistoryParams,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<
  AdapterResult<
    AhrefsCallResult<{ readonly metrics?: ReadonlyArray<Record<string, unknown>> }>
  >
> {
  const wireParams = buildBrandRadarHistoryWireParams(
    params,
    "/brand-radar/mentions-history"
  );
  return fetchAhrefsEndpoint(
    apiKey,
    "brand-radar-mentions-history",
    wireParams,
    ctx,
    { isFreeEndpoint: params.prompts === "custom" }
  );
}

// ----------------------------------------------------------------------------
// Story 20.2e — /brand-radar/impressions-overview + /brand-radar/mentions-overview
// ----------------------------------------------------------------------------
//
// Both endpoints share an identical param skeleton + the 5-bucket integer
// response shape: { metrics: [{ brand, only_target_brand,
// only_competitors_brands, target_and_competitors_brands, no_tracked_brands,
// total }] }. None of the 6 fields carry a per-field cost surcharge.

export interface BrandRadarImpressionsOverviewParams {
  /** REQUIRED non-empty per docs. Defaults to all 6 fields if route layer omits. */
  readonly select: ReadonlyArray<string>;
  readonly data_source: ReadonlyArray<BrandRadarDataSource>;
  readonly where?: string;
  readonly country?: string;
  readonly report_id?: string;
  readonly prompts?: "ahrefs" | "custom";
  readonly market?: string;
  readonly competitors?: string;
  readonly brand?: string;
  readonly output?: "json";
}

export type BrandRadarMentionsOverviewParams = BrandRadarImpressionsOverviewParams;

function buildBrandRadarOverviewWireParams(
  params: BrandRadarImpressionsOverviewParams,
  endpoint: string
): Record<string, string> {
  assertSelectNonEmpty(params.select, endpoint);
  assertBrandRadarDataSourceNonEmpty(params.data_source, endpoint);
  if (params.country !== undefined && !/^[a-z]{2}$/i.test(params.country)) {
    throw new TypeError(
      `Ahrefs ${endpoint}: \`country\` must be a 2-letter ISO 3166-1 alpha-2 code (e.g. "us").`
    );
  }

  const wireParams: Record<string, string> = {
    select: params.select.join(","),
    data_source: params.data_source.join(","),
    output: params.output ?? "json",
  };
  if (params.where) wireParams.where = params.where;
  if (params.country) wireParams.country = params.country.toLowerCase();
  if (params.report_id) wireParams.report_id = params.report_id;
  if (params.prompts) wireParams.prompts = params.prompts;
  if (params.market) wireParams.market = params.market;
  if (params.competitors) wireParams.competitors = params.competitors;
  if (params.brand) wireParams.brand = params.brand;
  return wireParams;
}

/**
 * `GET /v3/brand-radar/impressions-overview` — 5-bucket impression-share
 * breakdown across the requested AI clients. Response shape:
 * `{ metrics: [{ brand, only_target_brand, only_competitors_brands,
 * target_and_competitors_brands, no_tracked_brands, total }] }` (catalogue
 * rows 194-199).
 */
export async function fetchAhrefsBrandRadarImpressionsOverview(
  apiKey: string,
  params: BrandRadarImpressionsOverviewParams,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<
  AdapterResult<
    AhrefsCallResult<{ readonly metrics?: ReadonlyArray<Record<string, unknown>> }>
  >
> {
  const wireParams = buildBrandRadarOverviewWireParams(
    params,
    "/brand-radar/impressions-overview"
  );
  return fetchAhrefsEndpoint(
    apiKey,
    "brand-radar-impressions-overview",
    wireParams,
    ctx,
    { isFreeEndpoint: params.prompts === "custom" }
  );
}

/**
 * `GET /v3/brand-radar/mentions-overview` — same 5-bucket shape as
 * impressions-overview but counts brand mentions in AI responses (not
 * impression-share). Response shape identical (catalogue rows 200-205).
 */
export async function fetchAhrefsBrandRadarMentionsOverview(
  apiKey: string,
  params: BrandRadarMentionsOverviewParams,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<
  AdapterResult<
    AhrefsCallResult<{ readonly metrics?: ReadonlyArray<Record<string, unknown>> }>
  >
> {
  const wireParams = buildBrandRadarOverviewWireParams(
    params,
    "/brand-radar/mentions-overview"
  );
  return fetchAhrefsEndpoint(
    apiKey,
    "brand-radar-mentions-overview",
    wireParams,
    ctx,
    { isFreeEndpoint: params.prompts === "custom" }
  );
}

// ----------------------------------------------------------------------------
// Story 20.2e — /brand-radar/sov-overview + /brand-radar/sov-history
// ----------------------------------------------------------------------------
//
// Two distinct endpoints (NOT a single "share-of-voice" surface — the docs
// list them separately and the response shapes differ). Both ship a 2-field
// response — no `select` parameter.

export interface BrandRadarSovOverviewParams {
  readonly data_source: ReadonlyArray<BrandRadarDataSource>;
  readonly where?: string;
  readonly country?: string;
  readonly report_id?: string;
  readonly prompts?: "ahrefs" | "custom";
  readonly market?: string;
  readonly competitors?: string;
  readonly brand?: string;
  readonly output?: "json";
}

/**
 * `GET /v3/brand-radar/sov-overview` — current Share-of-Voice gauge. Response
 * shape: `{ metrics: [{ brand, share_of_voice }] }` where `share_of_voice` is
 * a float 0-1 (NOT a percentage). The headline agency-facing metric for the
 * Story 20.3 widget UI ("AI Share of Voice: 12% across ChatGPT/...").
 *
 * **Subscription-detection probe** — `discoverEndpoints` calls this endpoint
 * with `prompts=custom` + `data_source=chatgpt` + (no `select`) as the BR
 * add-on detection probe; the call is FREE (custom-prompts portion of the
 * response). On 403 + BR-flavored body, all 8 BR endpoints flip to
 * `subscription_required`.
 */
export async function fetchAhrefsBrandRadarSovOverview(
  apiKey: string,
  params: BrandRadarSovOverviewParams,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<
  AdapterResult<
    AhrefsCallResult<{ readonly metrics?: ReadonlyArray<Record<string, unknown>> }>
  >
> {
  assertBrandRadarDataSourceNonEmpty(params.data_source, "/brand-radar/sov-overview");
  if (params.country !== undefined && !/^[a-z]{2}$/i.test(params.country)) {
    throw new TypeError(
      "Ahrefs /brand-radar/sov-overview: `country` must be a 2-letter ISO 3166-1 alpha-2 code (e.g. \"us\")."
    );
  }

  const wireParams: Record<string, string> = {
    data_source: params.data_source.join(","),
    output: params.output ?? "json",
  };
  if (params.where) wireParams.where = params.where;
  if (params.country) wireParams.country = params.country.toLowerCase();
  if (params.report_id) wireParams.report_id = params.report_id;
  if (params.prompts) wireParams.prompts = params.prompts;
  if (params.market) wireParams.market = params.market;
  if (params.competitors) wireParams.competitors = params.competitors;
  if (params.brand) wireParams.brand = params.brand;

  return fetchAhrefsEndpoint(
    apiKey,
    "brand-radar-sov-overview",
    wireParams,
    ctx,
    { isFreeEndpoint: params.prompts === "custom" }
  );
}

export interface BrandRadarSovHistoryParams {
  readonly data_source: ReadonlyArray<BrandRadarDataSource>;
  /** REQUIRED YYYY-MM-DD start date. */
  readonly date_from: string;
  readonly date_to?: string;
  readonly where?: string;
  readonly country?: string;
  readonly report_id?: string;
  readonly prompts?: "ahrefs" | "custom";
  readonly market?: string;
  readonly brands?: string;
  readonly competitors?: string;
  readonly output?: "json";
}

/**
 * `GET /v3/brand-radar/sov-history` — Share-of-Voice time-series with
 * per-date nested-array shape: `{ metrics: [{ date, share_of_voice:
 * Array<{brand, value}> }] }`. The `share_of_voice` array is preserved
 * verbatim by the transformer — widgets render this as a stacked time-series
 * with brand-as-color (per AC #1 catalogue row 209).
 *
 * Critically, the transformer MUST NOT flatten this into one row per
 * (date, brand) pair — agencies expect the per-date list shape.
 */
export async function fetchAhrefsBrandRadarSovHistory(
  apiKey: string,
  params: BrandRadarSovHistoryParams,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<
  AdapterResult<
    AhrefsCallResult<{ readonly metrics?: ReadonlyArray<Record<string, unknown>> }>
  >
> {
  assertBrandRadarDataSourceNonEmpty(params.data_source, "/brand-radar/sov-history");
  if (!params.date_from || !BRAND_RADAR_DATE_REGEX.test(params.date_from)) {
    throw new TypeError(
      "Ahrefs /brand-radar/sov-history: `date_from` must be in YYYY-MM-DD format."
    );
  }
  if (params.date_to && !BRAND_RADAR_DATE_REGEX.test(params.date_to)) {
    throw new TypeError(
      "Ahrefs /brand-radar/sov-history: `date_to` must be in YYYY-MM-DD format."
    );
  }
  if (params.date_to) {
    const from = Date.parse(params.date_from);
    const to = Date.parse(params.date_to);
    if (Number.isFinite(from) && Number.isFinite(to) && from > to) {
      throw new TypeError(
        "Ahrefs /brand-radar/sov-history: `date_from` must precede `date_to`."
      );
    }
  }
  if (params.country !== undefined && !/^[a-z]{2}$/i.test(params.country)) {
    throw new TypeError(
      "Ahrefs /brand-radar/sov-history: `country` must be a 2-letter ISO 3166-1 alpha-2 code (e.g. \"us\")."
    );
  }

  const wireParams: Record<string, string> = {
    data_source: params.data_source.join(","),
    date_from: params.date_from,
    output: params.output ?? "json",
  };
  if (params.date_to) wireParams.date_to = params.date_to;
  if (params.where) wireParams.where = params.where;
  if (params.country) wireParams.country = params.country.toLowerCase();
  if (params.report_id) wireParams.report_id = params.report_id;
  if (params.prompts) wireParams.prompts = params.prompts;
  if (params.market) wireParams.market = params.market;
  if (params.brands) wireParams.brands = params.brands;
  if (params.competitors) wireParams.competitors = params.competitors;

  return fetchAhrefsEndpoint(
    apiKey,
    "brand-radar-sov-history",
    wireParams,
    ctx,
    { isFreeEndpoint: params.prompts === "custom" }
  );
}

// ----------------------------------------------------------------------------
// Story 20.2e — /brand-radar/cited-domains + /brand-radar/cited-pages
// ----------------------------------------------------------------------------
//
// Both endpoints carry the 2026 deprecations: `mentions` field deprecated
// 2026-02-10, `volume` field deprecated 2026-03-24. The transformer NULLs
// both at ingest regardless of wire payload (AC #5).
//
// `tracked_urls` is unique to `/cited-pages` — surfaces zero-citation rows
// for explicitly-tracked URLs (e.g. new product pages). No other BR endpoint
// has this param.

export interface BrandRadarCitedDomainsParams {
  readonly select: ReadonlyArray<string>;
  readonly data_source: ReadonlyArray<BrandRadarDataSource>;
  readonly where?: string;
  readonly limit?: number;
  readonly date?: string;
  readonly country?: string;
  readonly report_id?: string;
  readonly prompts?: "ahrefs" | "custom";
  readonly market?: string;
  readonly competitors?: string;
  readonly brand?: string;
  readonly output?: "json";
}

/**
 * `GET /v3/brand-radar/cited-domains` — top domains cited by AI responses.
 *
 * Response shape: `{ domains: [{ domain, mentions [DEPRECATED 2026-02-10],
 * pages, responses, volume [DEPRECATED 2026-03-24] }] }`. The transformer
 * NULLs both deprecated fields at ingest (per AC #5) so widgets never render
 * stale values.
 */
export async function fetchAhrefsBrandRadarCitedDomains(
  apiKey: string,
  params: BrandRadarCitedDomainsParams,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<
  AdapterResult<
    AhrefsCallResult<{ readonly domains?: ReadonlyArray<Record<string, unknown>> }>
  >
> {
  assertSelectNonEmpty(params.select, "/brand-radar/cited-domains");
  assertBrandRadarDataSourceNonEmpty(params.data_source, "/brand-radar/cited-domains");
  if (params.date !== undefined && !BRAND_RADAR_DATE_REGEX.test(params.date)) {
    throw new TypeError(
      "Ahrefs /brand-radar/cited-domains: `date` must be in YYYY-MM-DD format."
    );
  }
  if (params.country !== undefined && !/^[a-z]{2}$/i.test(params.country)) {
    throw new TypeError(
      "Ahrefs /brand-radar/cited-domains: `country` must be a 2-letter ISO 3166-1 alpha-2 code (e.g. \"us\")."
    );
  }

  const wireParams: Record<string, string> = {
    select: params.select.join(","),
    data_source: params.data_source.join(","),
    output: params.output ?? "json",
  };
  if (params.where) wireParams.where = params.where;
  if (params.limit !== undefined && params.limit > 0) {
    wireParams.limit = String(params.limit);
  }
  if (params.date) wireParams.date = params.date;
  if (params.country) wireParams.country = params.country.toLowerCase();
  if (params.report_id) wireParams.report_id = params.report_id;
  if (params.prompts) wireParams.prompts = params.prompts;
  if (params.market) wireParams.market = params.market;
  if (params.competitors) wireParams.competitors = params.competitors;
  if (params.brand) wireParams.brand = params.brand;

  return fetchAhrefsEndpoint(
    apiKey,
    "brand-radar-cited-domains",
    wireParams,
    ctx,
    { isFreeEndpoint: params.prompts === "custom" }
  );
}

export interface BrandRadarCitedPagesParams {
  readonly select: ReadonlyArray<string>;
  readonly data_source: ReadonlyArray<BrandRadarDataSource>;
  /**
   * UNIQUE to /cited-pages — surfaces zero-citation rows for URLs the customer
   * wants explicitly tracked even when they have zero AI-response coverage
   * yet (e.g. newly published product pages). Comma-separated list of
   * fully-qualified URLs.
   * Per docs verification 2026-05-05.
   */
  readonly tracked_urls?: string;
  readonly where?: string;
  readonly limit?: number;
  readonly date?: string;
  readonly country?: string;
  readonly report_id?: string;
  readonly prompts?: "ahrefs" | "custom";
  readonly market?: string;
  readonly competitors?: string;
  readonly brand?: string;
  readonly output?: "json";
}

/**
 * `GET /v3/brand-radar/cited-pages` — top pages cited by AI responses.
 *
 * Response shape: `{ pages: [{ url, mentions [DEPRECATED], responses, volume
 * [DEPRECATED] }] }`. Both deprecated fields are NULLed by the transformer
 * (per AC #5).
 *
 * **`tracked_urls` is unique to this endpoint.** Surfaces zero-coverage rows
 * for URLs the customer wants explicitly tracked (e.g. newly-published
 * product pages with no AI-response coverage so far so they chart progress
 * over time).
 */
export async function fetchAhrefsBrandRadarCitedPages(
  apiKey: string,
  params: BrandRadarCitedPagesParams,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<
  AdapterResult<
    AhrefsCallResult<{ readonly pages?: ReadonlyArray<Record<string, unknown>> }>
  >
> {
  assertSelectNonEmpty(params.select, "/brand-radar/cited-pages");
  assertBrandRadarDataSourceNonEmpty(params.data_source, "/brand-radar/cited-pages");
  if (params.date !== undefined && !BRAND_RADAR_DATE_REGEX.test(params.date)) {
    throw new TypeError(
      "Ahrefs /brand-radar/cited-pages: `date` must be in YYYY-MM-DD format."
    );
  }
  if (params.country !== undefined && !/^[a-z]{2}$/i.test(params.country)) {
    throw new TypeError(
      "Ahrefs /brand-radar/cited-pages: `country` must be a 2-letter ISO 3166-1 alpha-2 code (e.g. \"us\")."
    );
  }

  const wireParams: Record<string, string> = {
    select: params.select.join(","),
    data_source: params.data_source.join(","),
    output: params.output ?? "json",
  };
  if (params.tracked_urls) wireParams.tracked_urls = params.tracked_urls;
  if (params.where) wireParams.where = params.where;
  if (params.limit !== undefined && params.limit > 0) {
    wireParams.limit = String(params.limit);
  }
  if (params.date) wireParams.date = params.date;
  if (params.country) wireParams.country = params.country.toLowerCase();
  if (params.report_id) wireParams.report_id = params.report_id;
  if (params.prompts) wireParams.prompts = params.prompts;
  if (params.market) wireParams.market = params.market;
  if (params.competitors) wireParams.competitors = params.competitors;
  if (params.brand) wireParams.brand = params.brand;

  return fetchAhrefsEndpoint(
    apiKey,
    "brand-radar-cited-pages",
    wireParams,
    ctx,
    { isFreeEndpoint: params.prompts === "custom" }
  );
}

/**
 * **Code Review Pass 3 (P3-L9)** — pure function that decides how many
 * units to attribute to a Brand Radar response based on cost headers.
 * Extracted so the route-layer accumulator can be unit-tested directly.
 *
 * Returns `0` when:
 *  - `cacheStatus === "hit"` (cache-served, no Ahrefs unit cost).
 *  - `cacheStatus === "free"` (synthetic literal — `prompts=custom` calls
 *    are FREE per docs even when Ahrefs echoes a `costTotalExpected` value).
 *  - Both `costTotalActual` and `costTotalExpected` are non-positive.
 *
 * Otherwise prefers `costTotalActual` (Ahrefs's authoritative billed amount)
 * with `costTotalExpected` as fallback. Mirrors `extractActualCost` in
 * `ahrefs.ts:fetchMetrics` so route-layer + autonomous-sync agree on the
 * billed amount for any given response.
 */
export function brandRadarUnitsAttributable(cost: {
  readonly cacheStatus: AhrefsCostHeaders["cacheStatus"];
  readonly costTotalActual: number;
  readonly costTotalExpected: number;
}): number {
  if (cost.cacheStatus === "hit" || cost.cacheStatus === "free") return 0;
  if (cost.costTotalActual > 0) return cost.costTotalActual;
  if (cost.costTotalExpected > 0) return cost.costTotalExpected;
  return 0;
}

/**
 * Helper for the proxy-route layer: compute the expected per-call cost from a
 * `select` list using `BRAND_RADAR_FIELD_COSTS`. Returns 0 when none of the
 * fields are paid. Used by the unit-budget pre-flight gate.
 *
 * **Per-row multiplication invariant (Story 20.2e Code Review Pass 1, H1):**
 * the 10u surcharge on `response`/`links`/`volume` is per-included-field
 * **per-row**. A widget config passing `select=response,links,volume&limit=1000`
 * burns 30 × 1000 = 30_000 units. The pre-flight gate MUST account for `limit`
 * — passing `limit=1` (the spec default for AC #4) is correct ONLY when the
 * caller explicitly limits to 1 row; the proxy-route layer threads the
 * requested `limit` through to keep the gate accurate.
 *
 * Unrecognized fields cost 0 (they're either free or not on this endpoint).
 * The map's 3 keys (`response`, `links`, `volume`) are the only paid Brand
 * Radar fields per docs verification 2026-05-05.
 */
export function computeBrandRadarSelectCost(
  select: ReadonlyArray<string>,
  limit: number = 1
): number {
  let perRowTotal = 0;
  const costMap: Record<string, number> = BRAND_RADAR_FIELD_COSTS;
  for (const field of select) {
    const fieldCost = costMap[field];
    if (typeof fieldCost === "number") perRowTotal += fieldCost;
  }
  // Defensive clamp: limit must be ≥1; non-finite/negative limits collapse to 1
  // (worst-case minimum cost rather than negative/zero which would mask the
  // surcharge entirely).
  const safeLimit =
    Number.isFinite(limit) && limit > 0 ? Math.floor(limit) : 1;
  return perRowTotal * safeLimit;
}

/**
 * **Story 20.2e Code Review Pass 1 (M1)** — defensive `where`-clause
 * surcharge detection.
 *
 * Per AC #2 (line 55 of the spec): "[/sov-overview helper] detects `where`
 * substrings matching `response` and surcharges accordingly (defensive)".
 * The same defense applies to any BR endpoint that accepts `where` — a
 * widget building `where=response.contains("foo")` triggers the 10u-per-row
 * surcharge that the docs warn about, with zero pre-flight protection
 * unless we inspect the `where` expression.
 *
 * Detects token references to the 3 paid fields (`response`, `links`,
 * `volume`) in the `where` expression. Word-boundary regex prevents false
 * positives on substrings (`responses`, `response_count`, etc.). Returns
 * the per-row surcharge sum — multiply by `limit` at the gate.
 *
 * The pre-flight is intentionally conservative: it surcharges as if the
 * field were also in `select` even when the customer is only filtering on
 * it. This may slightly over-estimate cost (Ahrefs may not actually charge
 * for filter-only references on every endpoint) but the safety direction
 * is "reject early, never surprise the customer with a cap breach."
 *
 * **Code Review Pass 3 (P3-M5) — known over-count:** the regex matches
 * paid-field tokens INSIDE quoted string literals as well. A query like
 * `where=brand.contains("response")` is over-counted by 10u. The
 * over-count direction is intentional (safety > precision); a precise
 * implementation would require a full where-DSL parser, which is out of
 * scope. Documented here so future maintainers don't "fix" the false
 * positive in a way that opens an under-count vulnerability.
 */
export function computeBrandRadarWhereSurcharge(
  where: string | undefined
): number {
  if (!where || typeof where !== "string") return 0;
  let surcharge = 0;
  for (const field of Object.keys(BRAND_RADAR_FIELD_COSTS)) {
    const pattern = new RegExp(`\\b${field}\\b`);
    if (pattern.test(where)) {
      surcharge += (BRAND_RADAR_FIELD_COSTS as Record<string, number>)[field] ?? 0;
    }
  }
  return surcharge;
}

// ============================================================================
// Story 20.2f — Web Analytics overview surface (`/stats` + `/chart`)
// + dedicated `/v3/management/projects` discovery helper
// ============================================================================
//
// All 3 endpoints are FREE per docs.ahrefs.com verified 2026-05-06:
//   - /web-analytics/stats / /web-analytics/chart: "Requests to this endpoint
//     are free and do not consume any API units."
//   - /v3/management/projects: management endpoint, FREE per docs.
//
// The 6 catalogued `/stats` fields (rows 226-231 in
// `_bmad/bmm/data/integration-metrics/ahrefs.md`) are pageviews, visitors,
// visits, session_bounce_rate, avg_session_length, avg_session_duration_sec.
// Same 6 fields per-timestamp on `/chart` plus `timestamp` (ISO 8601).
//
// **`project_id` type conflict (ADR-D):** Management documents `string`, WA
// documents `integer`; values are numeric-castable in practice. WA helpers
// accept `string | number` and coerce; Management helper drops non-numeric.
//
// **No `select` parameter (AC #9):** NEITHER `/stats` NOR `/chart` accepts a
// `select` — both endpoints always return all 6 fields. Build-time tests
// assert that neither helper exposes a `select` field on its params interface.

/**
 * Story 20.2f — `granularity` enum for `/web-analytics/chart`.
 *
 * Exported so the proxy-route Zod schema can refine against the same const
 * (single source of truth — bumps to this list automatically widen route
 * validation without further code changes). Verified against
 * [docs.ahrefs.com/api/reference/web-analytics/get-chart] on 2026-05-06.
 */
export const WEB_ANALYTICS_GRANULARITY_VALUES = [
  "hourly",
  "daily",
  "weekly",
  "monthly",
] as const satisfies ReadonlyArray<string>;

export type WebAnalyticsGranularity =
  (typeof WEB_ANALYTICS_GRANULARITY_VALUES)[number];

/** Shared `YYYY-MM-DD` regex for Story 20.2f WA params. */
const WEB_ANALYTICS_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Code Review Pass 1 (H6) + Pass 3 (M-P3-3) — round-trip ISO date validator.
 * The bare regex `^\d{4}-\d{2}-\d{2}$` accepts impossible dates like
 * `2026-13-40` or `2026-02-30` (V8 silently rolls them over). This helper
 * round-trips through `Date` and asserts the formatted result equals the
 * input — catches Feb 30, month 13, day 40, leap-year drift.
 *
 * Exported (Pass 3 M-P3-3) so the proxy-route Zod schemas can re-use the
 * SAME validator as the helper layer — single source of truth, no drift.
 */
export function isValidWebAnalyticsIsoDate(s: string): boolean {
  if (!WEB_ANALYTICS_DATE_REGEX.test(s)) return false;
  const ms = Date.parse(`${s}T00:00:00Z`);
  if (!Number.isFinite(ms)) return false;
  // Normalize: format the parsed timestamp back to YYYY-MM-DD and require
  // round-trip equality. Catches Feb 30, month 13, etc. which V8 rolls over.
  const d = new Date(ms);
  const yyyy = d.getUTCFullYear().toString().padStart(4, "0");
  const mm = (d.getUTCMonth() + 1).toString().padStart(2, "0");
  const dd = d.getUTCDate().toString().padStart(2, "0");
  return `${yyyy}-${mm}-${dd}` === s;
}

export interface WebAnalyticsStatsParams {
  /**
   * REQUIRED. Numeric-castable; coerced to string for wire serialization
   * (per ADR-D — Management documents `string`, WA documents `integer`).
   */
  readonly project_id: string | number;
  /** Optional `YYYY-MM-DD`; regex-validated only when provided. */
  readonly from?: string;
  /** Optional `YYYY-MM-DD`; regex-validated only when provided. */
  readonly to?: string;
  /** Optional Ahrefs filter expression. */
  readonly where?: string;
  /** Optional. Default 1000 server-side. */
  readonly limit?: number;
  /** Optional, e.g. "pageviews:desc". */
  readonly order_by?: string;
  /** Default `"json"`. */
  readonly output?: "json" | "csv" | "xml" | "php";
  // NOTE: NO `select` field per AC #9 — endpoint always returns all 6 fields.
}

export interface WebAnalyticsChartParams {
  /** REQUIRED. Same coercion semantics as `/stats`. */
  readonly project_id: string | number;
  /** REQUIRED — TypeError synchronously on missing or out-of-enum. */
  readonly granularity: WebAnalyticsGranularity;
  readonly from?: string;
  readonly to?: string;
  readonly where?: string;
  readonly output?: "json" | "csv" | "xml" | "php";
  // NOTE: NO `select` field per AC #9 — endpoint always returns all 6 fields
  // per timestamp (plus `timestamp` itself).
}

/**
 * Throws TypeError synchronously when `project_id` is missing, empty after
 * coercion, or non-finite. Centralized so both WA helpers share identical
 * messaging.
 */
// Code Review Pass 2 (M-NEW-1) — single integer-validity regex shared with
// the route layer's `PROJECT_ID_NUMERIC_REGEX`. Rejects: leading zeros
// ("012345"), decimals ("0.5"), exponential ("1e3"), all-zero ("000"),
// negatives, and non-digits. Only `[1-9]\d*` survives.
const POSITIVE_INTEGER_REGEX = /^[1-9]\d*$/;

function assertWebAnalyticsProjectId(
  projectId: string | number | undefined,
  endpoint: string
): string {
  if (projectId === undefined || projectId === null) {
    throw new TypeError(
      `Ahrefs ${endpoint}: \`project_id\` is required (string | number).`
    );
  }
  if (typeof projectId === "number") {
    if (!Number.isFinite(projectId)) {
      throw new TypeError(
        `Ahrefs ${endpoint}: \`project_id\` must be a finite number.`
      );
    }
    // Code Review Pass 1 (M5) — Ahrefs project IDs are positive integers.
    // Code Review Pass 2 (M-NEW-1) — also reject decimals (0.5, 1.999) and
    // any non-integer-looking number value via Number.isInteger.
    if (!Number.isInteger(projectId) || projectId <= 0) {
      throw new TypeError(
        `Ahrefs ${endpoint}: \`project_id\` must be a positive integer.`
      );
    }
  }
  const coerced = String(projectId).trim();
  if (coerced.length === 0) {
    throw new TypeError(
      `Ahrefs ${endpoint}: \`project_id\` must be non-empty after coercion.`
    );
  }
  // Code Review Pass 2 (M-NEW-1) — single regex matching the route layer's
  // `PROJECT_ID_NUMERIC_REGEX`. Rejects "012345", "0.5", "1e3", "0", "-1".
  if (!POSITIVE_INTEGER_REGEX.test(coerced)) {
    throw new TypeError(
      `Ahrefs ${endpoint}: \`project_id\` must be a positive integer with no leading zeros (got "${coerced}").`
    );
  }
  return coerced;
}

function assertWebAnalyticsDateRange(
  from: string | undefined,
  to: string | undefined,
  endpoint: string
): void {
  if (from !== undefined && !isValidWebAnalyticsIsoDate(from)) {
    throw new TypeError(
      `Ahrefs ${endpoint}: \`from\` must be a valid YYYY-MM-DD calendar date when provided.`
    );
  }
  if (to !== undefined && !isValidWebAnalyticsIsoDate(to)) {
    throw new TypeError(
      `Ahrefs ${endpoint}: \`to\` must be a valid YYYY-MM-DD calendar date when provided.`
    );
  }
  if (from !== undefined && to !== undefined) {
    const fromMs = Date.parse(`${from}T00:00:00Z`);
    const toMs = Date.parse(`${to}T00:00:00Z`);
    if (Number.isFinite(fromMs) && Number.isFinite(toMs) && fromMs > toMs) {
      throw new TypeError(
        `Ahrefs ${endpoint}: \`from\` must precede \`to\`.`
      );
    }
  }
}

/**
 * `GET /v3/web-analytics/stats` — Web Analytics aggregate overview across the
 * provided date range (defaults to last 7 days when `from`/`to` omitted per
 * docs.ahrefs.com verified 2026-05-06).
 *
 * Story 20.2f AC #3 — **FREE endpoint, zero API units** per
 * [docs.ahrefs.com/api/reference/web-analytics/get-stats]. The helper passes
 * `isFreeEndpoint: true` so the cost-rollup wrapper short-circuits to zero.
 *
 * Response envelope: `{ stats: { pageviews, visitors, visits,
 * session_bounce_rate, avg_session_length, avg_session_duration_sec } }` —
 * **single aggregated object** across the date range (NOT an array).
 *
 * **No `select` per AC #9** — endpoint always returns all 6 fields.
 */
export async function fetchAhrefsWebAnalyticsStats(
  apiKey: string,
  params: WebAnalyticsStatsParams,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<
  AdapterResult<
    AhrefsCallResult<{ readonly stats?: Record<string, unknown> }>
  >
> {
  const projectIdWire = assertWebAnalyticsProjectId(
    params.project_id,
    "/web-analytics/stats"
  );
  assertWebAnalyticsDateRange(params.from, params.to, "/web-analytics/stats");

  const wireParams: Record<string, string> = {
    project_id: projectIdWire,
    output: params.output ?? "json",
  };
  if (params.from) wireParams.from = params.from;
  if (params.to) wireParams.to = params.to;
  if (params.where) wireParams.where = params.where;
  if (params.limit !== undefined && params.limit > 0) {
    wireParams.limit = String(params.limit);
  }
  if (params.order_by) wireParams.order_by = params.order_by;

  return fetchAhrefsEndpoint(
    apiKey,
    "web-analytics-stats",
    wireParams,
    ctx,
    { isFreeEndpoint: true }
  );
}

/**
 * `GET /v3/web-analytics/chart` — Web Analytics time-series. Same 6 fields as
 * `/stats` per timestamp, plus `timestamp` (ISO 8601 string per docs).
 *
 * Story 20.2f AC #3 — **FREE endpoint** per
 * [docs.ahrefs.com/api/reference/web-analytics/get-chart]. `granularity` is
 * REQUIRED (4-value enum); `project_id` REQUIRED (`string | number`).
 *
 * Response envelope: `{ points: Array<{ timestamp, pageviews, visitors,
 * visits, session_bounce_rate, avg_session_length, avg_session_duration_sec }> }`.
 *
 * Cardinality guard (against KV blob explosion) lives in the proxy-route
 * Zod schema — `granularity=hourly` over a 90-day range = 2160 points and
 * busts the per-call response budget. The helper itself does not enforce
 * the cardinality guard (it accepts any valid range) so direct callers
 * (autonomous sync, tests, future internal flows) opt-in to the same
 * defense at the call site.
 */
export async function fetchAhrefsWebAnalyticsChart(
  apiKey: string,
  params: WebAnalyticsChartParams,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<
  AdapterResult<
    AhrefsCallResult<{ readonly points?: ReadonlyArray<Record<string, unknown>> }>
  >
> {
  const projectIdWire = assertWebAnalyticsProjectId(
    params.project_id,
    "/web-analytics/chart"
  );
  if (
    params.granularity === undefined ||
    !WEB_ANALYTICS_GRANULARITY_VALUES.includes(params.granularity)
  ) {
    throw new TypeError(
      `Ahrefs /web-analytics/chart: \`granularity\` is required and must be one of ${WEB_ANALYTICS_GRANULARITY_VALUES.join(" | ")}.`
    );
  }
  assertWebAnalyticsDateRange(params.from, params.to, "/web-analytics/chart");

  const wireParams: Record<string, string> = {
    project_id: projectIdWire,
    granularity: params.granularity,
    output: params.output ?? "json",
  };
  if (params.from) wireParams.from = params.from;
  if (params.to) wireParams.to = params.to;
  if (params.where) wireParams.where = params.where;

  return fetchAhrefsEndpoint(
    apiKey,
    "web-analytics-chart",
    wireParams,
    ctx,
    { isFreeEndpoint: true }
  );
}

// ----------------------------------------------------------------------------
// Story 20.2f — `/v3/management/projects` discovery helper (NEW; ADR-E)
// ----------------------------------------------------------------------------
//
// Per docs verification 2026-05-06, `GET /v3/management/projects` returns the
// canonical project list with `web_analytics_data_key` per project (presence
// = WA enabled). This is DISTINCT from the existing
// `fetchAhrefsRankTrackerProjects` at api.ts:1089 which calls `/keyword-list`
// (legacy Rank Tracker management endpoint) — `/keyword-list` does NOT
// document `web_analytics_data_key`, so retrofitting it would silently lose
// the picker-UI signal.
//
// **`project_id` numeric-only guard (ADR-D):** Management documents
// `project_id: string` but empirically (per the existing Rank Tracker
// integration runtime) values are numeric-castable. The helper preserves the
// `Number.isFinite` guard from Story 20.2's existing helper (drops non-numeric
// IDs silently) — a follow-up story widens to `string | number` if runtime
// data demands it.

/**
 * Story 20.2f — public interface for `/v3/management/projects` rows.
 *
 * 8 documented fields plus optional `webAnalyticsDataKey?: string` (presence
 * indicates WA is enabled for that project). Distinct from the existing
 * `AhrefsRankTrackerProject` per ADR-E — the two endpoints return different
 * response shapes and combining them under one type would force optional
 * fields everywhere.
 */
export interface AhrefsManagementProject {
  readonly projectId: number;
  readonly projectName: string;
  /** Target URL (e.g. "https://example.com"). */
  readonly url: string;
  readonly mode: "exact" | "prefix" | "domain" | "subdomains";
  readonly protocol: "both" | "http" | "https";
  readonly access: "private" | "shared";
  /** Owner email; null when shared with the customer. */
  readonly ownedBy: string | null;
  readonly keywordCount: number;
  /** Presence (and non-empty) = Web Analytics enabled for this project. */
  readonly webAnalyticsDataKey?: string;
}

interface AhrefsManagementProjectsRaw {
  readonly projects?: ReadonlyArray<AhrefsManagementProjectRaw>;
}

interface AhrefsManagementProjectRaw {
  readonly project_id?: number | string;
  readonly project_name?: string;
  readonly url?: string;
  readonly target_url?: string;
  readonly mode?: string;
  readonly protocol?: string;
  readonly access?: string;
  readonly owned_by?: string | null;
  readonly keyword_count?: number;
  readonly web_analytics_data_key?: string;
}

// Code Review Pass 3 (N-P3-2) — TS-narrowing predicates instead of
// Set<string>.has() + as-cast. The literal-union becomes the single source
// of truth: a future enum-widening updates one place (the predicate's
// switch arms), not two (Set + as-cast).
function isMgmtMode(v: unknown): v is AhrefsManagementProject["mode"] {
  return v === "exact" || v === "prefix" || v === "domain" || v === "subdomains";
}
function isMgmtProtocol(v: unknown): v is AhrefsManagementProject["protocol"] {
  return v === "both" || v === "http" || v === "https";
}
function isMgmtAccess(v: unknown): v is AhrefsManagementProject["access"] {
  return v === "private" || v === "shared";
}

/**
 * `GET /v3/management/projects` — canonical project list with
 * `web_analytics_data_key` field for Web Analytics enablement detection.
 *
 * Story 20.2f AC #4 — **FREE endpoint, zero API units** per
 * [docs.ahrefs.com/api/docs/introduction]. Preserves the `Number.isFinite`
 * guard pattern from Story 20.2's `fetchAhrefsRankTrackerProjects`.
 *
 * **Response unification (defensive):** the helper accepts both
 * `{ projects: [...] }` (documented form) and a bare `[...]` array fallback
 * (defensive — mirrors the `fetchAhrefsRankTrackerProjects` posture for
 * Ahrefs API quirk tolerance). Unknown `mode`/`protocol`/`access` enum
 * values fall back to "domain"/"both"/"private" defensively so the picker
 * UI still renders the entry.
 *
 * **NOT modified — `fetchAhrefsRankTrackerProjects`** at api.ts:1089
 * continues to call `/keyword-list` for Rank Tracker discovery; per ADR-E
 * the two surfaces are kept structurally distinct (different response
 * shapes documented per Ahrefs).
 */
export async function fetchAhrefsManagementProjects(
  apiKey: string,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<
  AdapterResult<AhrefsCallResult<ReadonlyArray<AhrefsManagementProject>>>
> {
  const result = await ahrefsGet<
    AhrefsManagementProjectsRaw | ReadonlyArray<AhrefsManagementProjectRaw>
  >(
    apiKey,
    "/management/projects",
    { output: "json" },
    { ...ctx, endpoint: "/management/projects" },
    { isFreeEndpoint: true }
  );
  if (!result.success) return result;
  const raw = result.data.data;
  const list: ReadonlyArray<AhrefsManagementProjectRaw> = Array.isArray(raw)
    ? raw
    : (raw as AhrefsManagementProjectsRaw).projects ?? [];
  const projects: AhrefsManagementProject[] = [];
  for (const entry of list) {
    const rawId = entry.project_id;
    const idNum = typeof rawId === "string" ? Number(rawId) : rawId;
    // Preserve the Number.isFinite guard per ADR-D — drops non-numeric IDs
    // silently. A follow-up story widens to string|number if runtime
    // data demands it.
    if (typeof idNum !== "number" || !Number.isFinite(idNum)) continue;
    const mode = isMgmtMode(entry.mode) ? entry.mode : "domain";
    const protocol = isMgmtProtocol(entry.protocol) ? entry.protocol : "both";
    const access = isMgmtAccess(entry.access) ? entry.access : "private";
    // Code Review Pass 1 (L3) — Ahrefs documents `url` as required on
    // /management/projects responses. Defensive fallback to `target_url`
    // (older Ahrefs response shape) preserved for backward compat.
    const url = entry.url ?? entry.target_url ?? "";
    const project: AhrefsManagementProject = {
      projectId: idNum,
      projectName: entry.project_name ?? `Project ${idNum}`,
      url,
      mode,
      protocol,
      access,
      ownedBy: entry.owned_by ?? null,
      keywordCount:
        typeof entry.keyword_count === "number" &&
        Number.isFinite(entry.keyword_count)
          ? entry.keyword_count
          : 0,
      // No defensive defaulting: empty-string passes through and is correctly
      // excluded by the downstream `Boolean()` filter in
      // `filterAhrefsProjectsWithWebAnalytics` (round-3 hardening).
      ...(entry.web_analytics_data_key !== undefined
        ? { webAnalyticsDataKey: entry.web_analytics_data_key }
        : {}),
    };
    projects.push(project);
  }
  return adapterOk({ data: projects, cost: result.data.cost });
}

/**
 * Story 20.2f — pure helper that filters a project list to only those with
 * Web Analytics enabled (i.e. `webAnalyticsDataKey` present and truthy).
 *
 * `Boolean()` correctly excludes `undefined`, `null`, and `""` (empty
 * string) — the round-3 red-team hardening explicitly covered the
 * empty-string case so a fixture or wire payload returning `""` does NOT
 * leak into the WA picker UI.
 *
 * Used by the widget config UI (Story 20.3) when the customer is configuring
 * a Web Analytics widget — only WA-enabled projects appear in the picker
 * dropdown. Pure function (no API call); typically composed with the result
 * of `fetchAhrefsManagementProjects`.
 */
export function filterAhrefsProjectsWithWebAnalytics(
  projects: ReadonlyArray<AhrefsManagementProject>
): ReadonlyArray<AhrefsManagementProject> {
  return projects.filter((p) => Boolean(p.webAnalyticsDataKey));
}

// ============================================================================
// Story 35.8 — Subscription Info `/limits-and-usage` helper (FREE)
// ============================================================================
//
// Per [docs.ahrefs.com/api/reference/subscription-info/get-limits-and-usage]
// verified 2026-05-19:
//   - FREE endpoint ("Requests to this endpoint are free and do not consume
//     any API units.")
//   - NO query params accepted (the endpoint returns workspace + API-key
//     telemetry for the bearer-authenticated token; no `select`, no filter,
//     no pagination).
//   - Singleton object response (NOT an array): { subscription,
//     usage_reset_date, units_limit_workspace, units_usage_workspace,
//     units_limit_api_key, units_usage_api_key, api_key_expiration_date }.
//
// `AhrefsSubscriptionInfo` (the public response interface) is already exported
// above the validateAhrefsConnection helper — Story 35.8 reuses that shape.
// The new helper below is the agency-facing dashboard surface; the existing
// `validateAhrefsConnection` continues to serve connect-time validation,
// health-check cron, and key-rotation flows. Two named helpers around the same
// wire path is intentional: distinct semantic surfaces (validation vs.
// dashboard widget data) keep observability and rate-limiting per-surface.

/**
 * Param interface for `fetchAhrefsSubscriptionInfoLimitsAndUsage`. Empty
 * shape — the endpoint accepts no params per docs. Defined for signature
 * uniformity with the other 27 endpoint helpers.
 */
export interface SubscriptionInfoLimitsAndUsageParams {
  // intentionally empty — endpoint accepts no params
}

/**
 * `GET /v3/subscription-info/limits-and-usage` — workspace + API-key
 * telemetry for the agency-facing API-budget gauge widget (Story 20.4).
 *
 * FREE endpoint per [docs.ahrefs.com/api/reference/subscription-info/get-limits-and-usage]
 * verified 2026-05-19. Bundled into Ahrefs Connect — available to any
 * customer tier (Lite, Standard, Advanced, Enterprise). Passes
 * `isFreeEndpoint: true` so the cost-rollup short-circuits to zero.
 *
 * The endpoint accepts NO query parameters. The `_params` argument exists
 * only for signature uniformity with the other endpoint helpers; the wire
 * call always sends an empty params object.
 *
 * Returns the 7-field singleton response (NOT an array). Callers should
 * destructure directly from `result.data.data` rather than iterating.
 */
export async function fetchAhrefsSubscriptionInfoLimitsAndUsage(
  apiKey: string,
  _params: SubscriptionInfoLimitsAndUsageParams,
  ctx: Pick<AhrefsCallLogContext, "integrationId" | "tenantId">
): Promise<AdapterResult<AhrefsCallResult<AhrefsSubscriptionInfo>>> {
  return fetchAhrefsEndpoint<AhrefsSubscriptionInfo>(
    apiKey,
    "subscription-info-limits-and-usage",
    {},
    ctx,
    { isFreeEndpoint: true }
  );
}
